import React, { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment-timezone';
import InfoWithTooltip from './meta-ads/InfoWithTooltip';
import InterestTargetingInput from './meta-ads/InterestTargetingInput';

/**
 * CampaignReview - AI Draft Review Experience
 *
 * Design: Editorial precision. Black, white, grays only.
 * Typography does all the work. Every element earns its place.
 *
 * Structure follows Meta Ads Manager hierarchy:
 * - Overview: Campaign summary, market research, ticket math
 * - Targeting: All ad sets with targeting details
 * - Creative: All ads with copy variations
 *
 * Explanations: small info icon + tooltip next to key fields; full reasoning on hover.
 */

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// Safely render any value - handles nested objects
const safeRender = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    // If it's an array of primitives, join them
    if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
      return value.join(', ');
    }
    // If it's an array of objects, return null (needs special handling)
    return null;
  }
  if (typeof value === 'object') {
    // Don't render objects directly - needs special handling
    return null;
  }
  return String(value);
};

// Get field from fields array by api_key
const getField = (fields, apiKey) => {
  if (!Array.isArray(fields)) return null;
  return fields.find(f => f.api_key === apiKey);
};

// Get field value with fallback
const getFieldValue = (fields, apiKey, fallback = null) => {
  const field = getField(fields, apiKey);
  return field?.value ?? fallback;
};

// Format currency
const formatCurrency = (value) => {
  if (value == null) return null;
  // Convert cents to dollars (divide by 100)
  const dollars = typeof value === 'number' ? value / 100 : parseFloat(value) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(dollars);
};

// Format date
const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Valid flows per campaign objective (Meta Ads). User must select only valid combinations.
const OBJECTIVE_FLOWS = {
  OUTCOME_AWARENESS: {
    optimizationGoals: ['BRAND_AWARENESS', 'REACH'],
    billingEvents: ['IMPRESSIONS'],
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    pixelRequired: false,
    eventTypes: []
  },
  OUTCOME_TRAFFIC: {
    optimizationGoals: ['LINK_CLICKS', 'CLICKS'],
    billingEvents: ['LINK_CLICKS', 'CLICKS'],
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    pixelRequired: false,
    eventTypes: ['PURCHASE', 'VIEW_CONTENT', 'LEAD', 'COMPLETE_REGISTRATION']
  },
  OUTCOME_ENGAGEMENT: {
    optimizationGoals: ['POST_ENGAGEMENT', 'PAGE_ENGAGEMENT', 'EVENT_RESPONSES'],
    billingEvents: ['IMPRESSIONS'],
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    pixelRequired: false,
    eventTypes: ['PURCHASE']
  },
  OUTCOME_SALES: {
    optimizationGoals: ['OFFSITE_CONVERSIONS'],
    billingEvents: ['IMPRESSIONS'],
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    pixelRequired: true,
    eventTypes: ['PURCHASE', 'ADD_TO_CART', 'INITIATE_CHECKOUT', 'COMPLETE_REGISTRATION', 'LEAD', 'VIEW_CONTENT']
  }
};

const getObjectiveFlow = (objective) => {
  if (!objective) return null;
  return OBJECTIVE_FLOWS[String(objective).toUpperCase()] || null;
};

// Convert UTC date string to local datetime-local format (YYYY-MM-DDTHH:mm)
const utcToLocalDatetime = (utcDateString) => {
  if (!utcDateString) return '';
  const date = new Date(utcDateString);
  if (isNaN(date.getTime())) return '';
  // Get local date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Format API date (ISO with or without Z) as datetime-local string in event timezone (for Targeting tab, same as Edit Post)
const formatDateInEventTz = (dateString, eventTz) => {
  if (!dateString) return '';
  const tz = eventTz || 'UTC';
  const m = moment(dateString);
  if (!m.isValid()) return '';
  return m.tz(tz).format('YYYY-MM-DDTHH:mm');
};

// Convert local datetime-local format (YYYY-MM-DDTHH:mm) to UTC ISO string (browser local - for non–event-TZ use)
const localDatetimeToUTC = (localDatetimeString) => {
  if (!localDatetimeString) return null;
  const date = new Date(localDatetimeString);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
};

// Convert datetime-local string (interpreted in event timezone) to UTC ISO string (same as Edit Post)
const eventTzDatetimeToUTC = (localDatetimeString, eventTz) => {
  if (!localDatetimeString) return null;
  const tz = eventTz || 'UTC';
  const m = moment.tz(localDatetimeString, 'YYYY-MM-DDTHH:mm', tz);
  if (!m.isValid()) return null;
  return m.utc().toISOString();
};

// Now in event timezone as datetime-local string (for input min, same as Edit Post nowLocalInputForTz)
const nowInEventTzForInput = (eventTz) => {
  const tz = eventTz || 'UTC';
  return moment.tz(new Date(), tz).format('YYYY-MM-DDTHH:mm');
};

// Validate that start and end (datetime-local strings in event TZ) are in the future (same idea as Edit Post isFutureMinute)
const validateScheduleInFuture = (startDateStr, endDateStr, eventTz) => {
  const tz = eventTz || 'UTC';
  const now = moment.tz(new Date(), tz);
  if (startDateStr) {
    const start = moment.tz(startDateStr, 'YYYY-MM-DDTHH:mm', tz);
    if (!start.isValid()) return { valid: false, message: 'Start date & time must be in the future (event timezone).' };
    if (!start.isAfter(now, 'minute')) return { valid: false, message: 'Start date & time must be in the future (event timezone).' };
  }
  if (endDateStr) {
    const end = moment.tz(endDateStr, 'YYYY-MM-DDTHH:mm', tz);
    if (!end.isValid()) return { valid: false, message: 'End date & time must be in the future (event timezone).' };
    if (!end.isAfter(now, 'minute')) return { valid: false, message: 'End date & time must be in the future (event timezone).' };
  }
  if (startDateStr && endDateStr) {
    const start = moment.tz(startDateStr, 'YYYY-MM-DDTHH:mm', tz);
    const end = moment.tz(endDateStr, 'YYYY-MM-DDTHH:mm', tz);
    if (!end.isAfter(start, 'minute')) return { valid: false, message: 'End date & time must be after start date & time.' };
  }
  return { valid: true };
};

// Format percentage
const formatPercent = (value) => {
  if (value == null) return null;
  return `${Math.round(value * 100)}%`;
};

// CTA display names - Event Ticket CTAs
const CTA_LABELS = {
  'BUY_TICKETS': 'Buy Tickets',
  'GET_EVENT_TICKETS': 'Get Event Tickets',
  'BUY_NOW': 'Buy Now',
  'BOOK_NOW': 'Book Now',
  'ORDER_NOW': 'Order Now',
  'LEARN_MORE': 'Learn More',
  'SEE_DETAILS': 'See Details',
  'EVENT_RSVP': 'Event RSVP',
  'REMIND_ME': 'Remind Me',
  // Legacy lowercase support
  'learn_more': 'Learn More',
  'get_tickets': 'Get Tickets',
  'sign_up': 'Sign Up',
  'book_now': 'Book Now',
  'shop_now': 'Shop Now',
};

// Valid CTA types for event tickets
const VALID_CTA_TYPES = [
  'BUY_TICKETS',
  'GET_EVENT_TICKETS',
  'BUY_NOW',
  'BOOK_NOW',
  'ORDER_NOW',
  'LEARN_MORE',
  'SEE_DETAILS',
  'EVENT_RSVP',
  'REMIND_ME'
];

// Confidence badge colors
const CONFIDENCE_STYLES = {
  high: 'cr-confidence--high',
  medium: 'cr-confidence--medium',
  low: 'cr-confidence--low',
};

// ═══════════════════════════════════════════════════════════════════════════
// INFO ICON + TOOLTIP (full reasoning on hover) – rendered in portal to avoid
// parent overflow clipping (e.g. .cr-drawer__content overflow-y: auto)
// ═══════════════════════════════════════════════════════════════════════════

// InfoWithTooltip imported from ./meta-ads/InfoWithTooltip

// ═══════════════════════════════════════════════════════════════════════════
// CONFIDENCE BADGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const ConfidenceBadge = ({ level }) => {
  if (!level) return null;
  return (
    <span className={`cr-confidence ${CONFIDENCE_STYLES[level] || ''}`}>
      {level}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FIELD ROW COMPONENT WITH EXPLANATION
// ═══════════════════════════════════════════════════════════════════════════

const FieldRow = ({ label, value, summary, explanation, confidence, sources, required = false }) => {
  const displayValue = safeRender(value);
  const hasValue = displayValue !== null && displayValue !== '';
  const reasoningText = explanation || summary;

  return (
    <div className={`cr-field ${!hasValue ? 'cr-field--empty' : ''}`}>
      <div className="cr-field__header">
        <span className="cr-field__label-wrap">
          <span className="cr-field__label">{label}</span>
          {reasoningText && (
            <InfoWithTooltip text={reasoningText} sources={sources} />
          )}
        </span>
        <ConfidenceBadge level={confidence} />
      </div>

      <div className="cr-field__value">
        {hasValue ? displayValue : (
          <span className="cr-field__empty">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 4v3m0 3h.01M13 7A6 6 0 111 7a6 6 0 0112 0z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            Needs input
          </span>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COPY VARIATIONS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const CopyVariations = ({ label, variations, explanation, selectedIndex: externalSelectedIndex, onSelectionChange, disabled = false }) => {
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(externalSelectedIndex !== undefined ? externalSelectedIndex : 0);

  // Sync internal state with external selectedIndex when it changes
  useEffect(() => {
    if (externalSelectedIndex !== undefined) {
      setInternalSelectedIndex(externalSelectedIndex);
    }
  }, [externalSelectedIndex]);

  // Use external selectedIndex if provided, otherwise use internal state
  const selectedIndex = externalSelectedIndex !== undefined ? externalSelectedIndex : internalSelectedIndex;

  const handleIndexChange = (newIndex) => {
    if (disabled) {
      return; // Don't allow changes when disabled
    }
    setInternalSelectedIndex(newIndex);
    if (onSelectionChange) {
      onSelectionChange(newIndex);
    }
  };

  if (!variations || variations.length === 0) {
    return (
      <div className="cr-field cr-field--empty">
        <div className="cr-field__header">
          <span className="cr-field__label">{label}</span>
        </div>
        <div className="cr-field__value">
          <span className="cr-field__empty">No variations</span>
        </div>
      </div>
    );
  }

  const reasoningText = explanation && safeRender(explanation);

  return (
    <div className="cr-copy-variations">
      <div className="cr-copy-variations__header">
        <span className="cr-field__label-wrap">
          <span className="cr-field__label">{label}</span>
          {reasoningText && <InfoWithTooltip text={reasoningText} />}
        </span>
        <span className="cr-copy-variations__count">{variations.length} variations</span>
      </div>

      {/* Variation pills */}
      <div className="cr-copy-variations__pills">
        {variations.map((_, index) => (
          <button
            key={index}
            className={`cr-copy-variations__pill ${selectedIndex === index ? 'cr-copy-variations__pill--active' : ''} ${disabled ? 'cr-copy-variations__pill--disabled' : ''}`}
            onClick={() => handleIndexChange(index)}
            disabled={disabled}
            title={disabled ? 'Cannot change variations when ad is paused or scheduled' : ''}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {/* Active variation */}
      <div className="cr-copy-variations__content">
        {safeRender(variations[selectedIndex]) || JSON.stringify(variations[selectedIndex])}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AD SET CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

// Helper function to format attribution spec (JSONB type: default {})
// Can be an object, array of objects, or string representation
const formatAttributionSpec = (value) => {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
    return 'Not set';
  }
  
  try {
    let parsed = value;
    
    // If it's a string, try to parse it as JSON (fallback for string representations)
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          parsed = JSON.parse(value);
        } catch (parseError) {
          return value; // Return as-is if parsing fails
        }
      } else {
        return value; // Not JSON, return as-is
      }
    }
    
    // Handle array of objects (JSONB can be an array)
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map(spec => {
        const eventType = spec.event_type || spec['event_type'] || 'Unknown';
        const windowDays = spec.window_days || spec['window_days'] || 0;
        const eventTypeFormatted = eventType.replace(/_/g, ' ').toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return `${eventTypeFormatted} (${windowDays} day${windowDays !== 1 ? 's' : ''} window)`;
      }).join(', ');
    }
    
    // Handle single object (JSONB default is {})
    if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null) {
      // Check if it has event_type and window_days (single spec object)
      if (parsed.event_type || parsed['event_type']) {
        const eventType = parsed.event_type || parsed['event_type'] || 'Unknown';
        const windowDays = parsed.window_days || parsed['window_days'] || 0;
        const eventTypeFormatted = eventType.replace(/_/g, ' ').toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return `${eventTypeFormatted} (${windowDays} day${windowDays !== 1 ? 's' : ''} window)`;
      }
      // If it's an object but doesn't have the expected structure, format it
      return JSON.stringify(parsed);
    }
    
    // Fallback
    return String(value);
  } catch (e) {
    return String(value);
  }
};

// Helper function to format array values (platforms, placements, etc.)
// JSONB types: genders (default []), device_platforms (default {}), publisher_platforms (default {})
const formatArrayValue = (value) => {
  if (!value) return 'Not set';
  
  // Handle empty arrays/objects
  if (Array.isArray(value) && value.length === 0) return 'Not set';
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return 'Not set';
  }
  
  try {
    let parsed = value;
    
    // If it's already an array (JSONB array like genders), use it directly
    if (Array.isArray(value)) {
      parsed = value;
    }
    // If it's an object (JSONB object like device_platforms, publisher_platforms)
    else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      // Check if it has a 'value' key (sometimes stored as {value: [...]})
      if (value.value && Array.isArray(value.value)) {
        parsed = value.value;
      }
      // If it's an object with array-like structure, try to extract values
      else if (Object.keys(value).length > 0) {
        // Try to get the first array value if it exists
        const firstKey = Object.keys(value)[0];
        if (Array.isArray(value[firstKey])) {
          parsed = value[firstKey];
        } else {
          // If it's an object with string keys, format the keys
          return Object.keys(value).map(key => {
            const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, ' ');
            return formattedKey;
          }).join(', ');
        }
      } else {
        return 'Not set';
      }
    }
    // If it's a string, try to parse it as JSON (fallback)
    else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          parsed = JSON.parse(value);
        } catch (parseError) {
          // If parsing fails, format as single string
          return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replace(/_/g, ' ');
        }
      } else {
        // Single string value, format and return
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replace(/_/g, ' ');
      }
    }
    
    // Handle array (from JSONB array or parsed from string)
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return 'Not set';
      
      return parsed.map(item => {
        // Capitalize first letter and replace underscores with spaces
        if (typeof item === 'string') {
          return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase().replace(/_/g, ' ');
        }
        return String(item);
      }).join(', ');
    }
    
    // Handle single string
    if (typeof parsed === 'string') {
      return parsed.charAt(0).toUpperCase() + parsed.slice(1).toLowerCase().replace(/_/g, ' ');
    }
    
    // Fallback
    return String(value);
  } catch (e) {
    return String(value);
  }
};

const FacebookLocationRow = ({
  facebookLocation,
  radius,
  hasPreciseLocation,
  currentLatitude,
  currentLongitude,
  campaignId,
  adSetId,
  onRefetchCampaign,
  locationExplanation,
  scopeUrl
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [latInput, setLatInput] = useState('');
  const [lonInput, setLonInput] = useState('');
  const [radiusInput, setRadiusInput] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const displayRadius = radius != null && radius !== '' ? String(radius) : null;

  const openModal = (edit) => {
    setIsEditMode(!!edit);
    setLatInput(edit && currentLatitude != null ? String(currentLatitude) : '');
    setLonInput(edit && currentLongitude != null ? String(currentLongitude) : '');
    setRadiusInput(edit && radius != null && radius !== '' ? String(radius) : '25');
    setErrorMessage(null);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const lat = latInput.trim() ? parseFloat(latInput, 10) : null;
    const lon = lonInput.trim() ? parseFloat(lonInput, 10) : null;
    const radiusVal = radiusInput.trim() ? parseInt(radiusInput, 10) : null;
    const hasLat = lat != null && !Number.isNaN(lat) && lat >= -90 && lat <= 90;
    const hasLon = lon != null && !Number.isNaN(lon) && lon >= -180 && lon <= 180;
    const hasRadius = radiusVal != null && !Number.isNaN(radiusVal) && radiusVal > 0;
    if (!campaignId || !adSetId || !onRefetchCampaign || !hasLat || !hasLon || !hasRadius) {
      setErrorMessage('Please enter valid latitude, longitude, and radius (miles).');
      return;
    }
    setErrorMessage(null);
    setResolveLoading(true);
    try {
      const res = await fetch(scopeUrl('/api/facebook/update_location'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          ad_set_id: adSetId,
          latitude: lat,
          longitude: lon,
          radius: radiusVal
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setModalOpen(false);
        setLatInput('');
        setLonInput('');
        setRadiusInput('');
        await onRefetchCampaign();
      } else {
        setErrorMessage(data.error || 'Failed to update location');
      }
    } catch (e) {
      setErrorMessage(e.message || 'Failed to update location');
    } finally {
      setResolveLoading(false);
    }
  };

  const hasValidLat = latInput.trim() && !Number.isNaN(parseFloat(latInput, 10));
  const hasValidLon = lonInput.trim() && !Number.isNaN(parseFloat(lonInput, 10));
  const hasValidRadius = radiusInput.trim() && parseInt(radiusInput, 10) > 0;
  const canSubmit = hasValidLat && hasValidLon && hasValidRadius;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {facebookLocation ? (
            <FieldRow label="Facebook Location" value={facebookLocation} explanation={locationExplanation} />
          ) : (
            <FieldRow label="Facebook Location" value="US" explanation={locationExplanation} />
          )}
          {hasPreciseLocation && displayRadius != null && (
            <FieldRow label="Radius (miles)" value={displayRadius} />
          )}
        </div>
        <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
          {!hasPreciseLocation && (
            <button
              type="button"
              onClick={() => openModal(false)}
              style={{
                padding: '0.4rem 0.75rem',
                fontSize: '0.8rem',
                fontWeight: 500,
                border: '1px solid #1A1A19',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#1A1A19',
                cursor: 'pointer'
              }}
            >
              Set precise location
            </button>
          )}
          {hasPreciseLocation && (
            <button
              type="button"
              onClick={() => openModal(true)}
              style={{
                padding: '0.4rem 0.75rem',
                fontSize: '0.8rem',
                fontWeight: 500,
                border: '1px solid #1A1A19',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#1A1A19',
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
      {modalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)'
          }}
          onClick={() => !resolveLoading && setModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '1.5rem',
              borderRadius: '8px',
              minWidth: '320px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>
              {isEditMode ? 'Edit location' : 'Set location'}
            </h4>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#555' }}>
              Enter latitude, longitude, and radius (miles) to target ads to that area.
            </p>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', color: '#555' }}>
                Latitude
              </label>
              <input
                type="number"
                step="any"
                min={-90}
                max={90}
                value={latInput}
                onChange={(e) => { setLatInput(e.target.value); setErrorMessage(null); }}
                placeholder="e.g. 38.7946"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${errorMessage ? '#c00' : '#ddd'}`,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', color: '#555' }}>
                Longitude
              </label>
              <input
                type="number"
                step="any"
                min={-180}
                max={180}
                value={lonInput}
                onChange={(e) => { setLonInput(e.target.value); setErrorMessage(null); }}
                placeholder="e.g. -106.5348"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${errorMessage ? '#c00' : '#ddd'}`,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', color: '#555' }}>
                Radius (miles)
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={radiusInput}
                onChange={(e) => { setRadiusInput(e.target.value); setErrorMessage(null); }}
                placeholder="25"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${errorMessage ? '#c00' : '#ddd'}`,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {errorMessage && (
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#c00' }}>{errorMessage}</p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={resolveLoading}
                onClick={() => !resolveLoading && setModalOpen(false)}
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: resolveLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resolveLoading || !canSubmit}
                onClick={handleSubmit}
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #1A1A19',
                  borderRadius: '4px',
                  background: '#1A1A19',
                  color: '#fff',
                  cursor: resolveLoading || !canSubmit ? 'not-allowed' : 'pointer'
                }}
              >
                {resolveLoading ? (isEditMode ? 'Updating…' : 'Setting…') : (isEditMode ? 'Update' : 'Set')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdSetCard = ({ adSet, index, isExpanded, onToggle, campaign, fullResponse, onSaveAdSet, pixels = [], onRefetchCampaign, onSyncPixels, syncPixelsLoading = false, pixelsLoading = false, scopeUrl }) => {
  const fields = adSet.fields || [];
  const ads = adSet.ads || [];

  // Event timezone for displaying start/end date (same as Edit Post modal)
  const eventTimezone = campaign?.meta_ads_ai_report?.event?.timezone || 'UTC';

  // Editable when ad set is draft or failed (same as Creative tab for ads)
  const isAdSetEditable = (() => {
    const s = String(adSet?.status || '').toLowerCase();
    return s === 'draft' || s === 'failed';
  })();

  // Get the first ad ID to use for updating the ad set
  const firstAdId = ads.length > 0 ? getFieldValue(ads[0].fields || [], 'ad.id') : null;
  
  // State for editable ad set fields
  const [editableValues, setEditableValues] = useState(() => {
    // Initialize from database values
    // Normalize to uppercase for consistency with select options
    const destinationTypeRaw = getFieldValue(fields, 'ad_set.destination_type') || '';
    const destinationType = destinationTypeRaw ? destinationTypeRaw.toUpperCase() : '';
    
    const publisherPlatforms = getFieldValue(fields, 'ad_set.publisher_platforms');
    const devicePlatforms = getFieldValue(fields, 'ad_set.device_placements');
    
    const billingEventRaw = getFieldValue(fields, 'ad_set.billing_event') || '';
    const billingEvent = billingEventRaw ? billingEventRaw.toUpperCase() : '';
    
    const optimizationGoalRaw = getFieldValue(fields, 'ad_set.optimization_goal') || '';
    const optimizationGoal = optimizationGoalRaw ? optimizationGoalRaw.toUpperCase() : '';
    
    const bidStrategyRaw = getFieldValue(fields, 'ad_set.bid_strategy') || 'LOWEST_COST_WITHOUT_CAP';
    const bidStrategy = bidStrategyRaw ? bidStrategyRaw.toUpperCase() : 'LOWEST_COST_WITHOUT_CAP';
    
    // Get raw genders array (for editing) - prefer raw array over formatted string
    const gendersRaw = getFieldValue(fields, 'ad_set.genders_raw');
    const genders = gendersRaw !== null && gendersRaw !== undefined ? gendersRaw : getFieldValue(fields, 'ad_set.genders');
    
    // Parse JSONB values
    let publisherPlatformsArray = [];
    if (publisherPlatforms) {
      if (Array.isArray(publisherPlatforms)) {
        publisherPlatformsArray = publisherPlatforms;
      } else if (typeof publisherPlatforms === 'string') {
        try {
          publisherPlatformsArray = JSON.parse(publisherPlatforms);
        } catch (e) {
          publisherPlatformsArray = [];
        }
      } else if (publisherPlatforms.value && Array.isArray(publisherPlatforms.value)) {
        publisherPlatformsArray = publisherPlatforms.value;
      }
    }
    
    let devicePlatformsArray = [];
    if (devicePlatforms) {
      if (Array.isArray(devicePlatforms)) {
        devicePlatformsArray = devicePlatforms;
      } else if (typeof devicePlatforms === 'string') {
        try {
          devicePlatformsArray = JSON.parse(devicePlatforms);
        } catch (e) {
          devicePlatformsArray = [];
        }
      } else if (devicePlatforms.value && Array.isArray(devicePlatforms.value)) {
        devicePlatformsArray = devicePlatforms.value;
      }
    }
    // Default to both mobile and desktop when no device platforms are set
    if (devicePlatformsArray.length === 0) {
      devicePlatformsArray = ['mobile', 'desktop'];
    }
    
    let gendersArray = [];
    // Use raw array if available, otherwise try to parse
    if (gendersRaw !== null && gendersRaw !== undefined) {
      // Raw array is available, use it directly
      if (Array.isArray(gendersRaw)) {
        gendersArray = gendersRaw.map(g => {
          const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
          return (num === 1 || num === 2) ? num : null;
        }).filter(g => g !== null);
      }
    } else if (genders) {
      // Fallback: try to parse from formatted string or other formats
      if (Array.isArray(genders)) {
        // Normalize to numbers and filter out invalid values
        gendersArray = genders.map(g => {
          const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
          return (num === 1 || num === 2) ? num : null;
        }).filter(g => g !== null);
      } else if (typeof genders === 'string') {
        try {
          const parsed = JSON.parse(genders);
          if (Array.isArray(parsed)) {
            gendersArray = parsed.map(g => {
              const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
              return (num === 1 || num === 2) ? num : null;
            }).filter(g => g !== null);
          }
        } catch (e) {
          gendersArray = [];
        }
      }
    }
    
    // Get ad set name
    const adSetName = getFieldValue(fields, 'ad_set.name', `Ad Set ${index + 1}`);
    const ageMinVal = getFieldValue(fields, 'ad_set.age_min') ?? adSet.age_min;
    const ageMaxVal = getFieldValue(fields, 'ad_set.age_max') ?? adSet.age_max;

    // Get start and end dates (display in event timezone, same as Edit Post)
    const startDate = getFieldValue(fields, 'ad_set.start_date');
    const endDate = getFieldValue(fields, 'ad_set.end_date');
    // Pixel (for conversion objectives)
    const pixelId = adSet.pixel_id ?? getFieldValue(fields, 'ad_set.pixel_id');
    const customEventType = adSet.custom_event_type ?? getFieldValue(fields, 'ad_set.custom_event_type');
    const tz = campaign?.meta_ads_ai_report?.event?.timezone || 'UTC';
    return {
      destinationType,
      publisherPlatforms: publisherPlatformsArray,
      devicePlatforms: devicePlatformsArray,
      billingEvent,
      optimizationGoal,
      bidStrategy,
      bidAmount: '',
      genders: gendersArray,
      name: adSetName,
      ageMin: ageMinVal != null ? Number(ageMinVal) : '',
      ageMax: ageMaxVal != null ? Number(ageMaxVal) : '',
      startDate: formatDateInEventTz(startDate, tz),
      endDate: formatDateInEventTz(endDate, tz),
      pixelId: pixelId || '',
      customEventType: customEventType || '',
      interests: (() => {
        const raw = getFieldValue(fields, 'ad_set.detailed_targeting.interests');
        if (Array.isArray(raw)) return raw.filter(i => i && i.id);
        return [];
      })()
    };
  });
  
  const [saving, setSaving] = useState(false);

  // Sync editable values when fields change (e.g., after save or data refresh)
  useEffect(() => {
    const destinationTypeRaw = getFieldValue(fields, 'ad_set.destination_type') || '';
    const destinationType = destinationTypeRaw ? destinationTypeRaw.toUpperCase() : '';
    
    const billingEventRaw = getFieldValue(fields, 'ad_set.billing_event') || '';
    const billingEvent = billingEventRaw ? billingEventRaw.toUpperCase() : '';
    
    const optimizationGoalRaw = getFieldValue(fields, 'ad_set.optimization_goal') || '';
    const optimizationGoal = optimizationGoalRaw ? optimizationGoalRaw.toUpperCase() : '';
    
    const bidStrategyRaw = getFieldValue(fields, 'ad_set.bid_strategy') || 'LOWEST_COST_WITHOUT_CAP';
    const bidStrategy = bidStrategyRaw ? bidStrategyRaw.toUpperCase() : 'LOWEST_COST_WITHOUT_CAP';
    
    const publisherPlatforms = getFieldValue(fields, 'ad_set.publisher_platforms');
    const devicePlatforms = getFieldValue(fields, 'ad_set.device_placements');
    // Get raw genders array (for editing) - prefer raw array over formatted string
    const gendersRaw = getFieldValue(fields, 'ad_set.genders_raw');
    const genders = gendersRaw !== null && gendersRaw !== undefined ? gendersRaw : getFieldValue(fields, 'ad_set.genders');
    
    // Parse JSONB values
    let publisherPlatformsArray = [];
    if (publisherPlatforms) {
      if (Array.isArray(publisherPlatforms)) {
        publisherPlatformsArray = publisherPlatforms;
      } else if (typeof publisherPlatforms === 'string') {
        try {
          publisherPlatformsArray = JSON.parse(publisherPlatforms);
        } catch (e) {
          publisherPlatformsArray = [];
        }
      } else if (publisherPlatforms.value && Array.isArray(publisherPlatforms.value)) {
        publisherPlatformsArray = publisherPlatforms.value;
      }
    }
    
    let devicePlatformsArray = [];
    if (devicePlatforms) {
      if (Array.isArray(devicePlatforms)) {
        devicePlatformsArray = devicePlatforms;
      } else if (typeof devicePlatforms === 'string') {
        try {
          devicePlatformsArray = JSON.parse(devicePlatforms);
        } catch (e) {
          devicePlatformsArray = [];
        }
      } else if (devicePlatforms.value && Array.isArray(devicePlatforms.value)) {
        devicePlatformsArray = devicePlatforms.value;
      }
    }
    // Default to both mobile and desktop when no device platforms are set
    if (devicePlatformsArray.length === 0) {
      devicePlatformsArray = ['mobile', 'desktop'];
    }
    
    let gendersArray = [];
    // Use raw array if available, otherwise try to parse
    if (gendersRaw !== null && gendersRaw !== undefined) {
      // Raw array is available, use it directly
      if (Array.isArray(gendersRaw)) {
        gendersArray = gendersRaw.map(g => {
          const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
          return (num === 1 || num === 2) ? num : null;
        }).filter(g => g !== null);
      }
    } else if (genders) {
      // Fallback: try to parse from formatted string or other formats
      if (Array.isArray(genders)) {
        // Normalize to numbers and filter out invalid values
        gendersArray = genders.map(g => {
          const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
          return (num === 1 || num === 2) ? num : null;
        }).filter(g => g !== null);
      } else if (typeof genders === 'string') {
        try {
          const parsed = JSON.parse(genders);
          if (Array.isArray(parsed)) {
            gendersArray = parsed.map(g => {
              const num = typeof g === 'string' ? parseInt(g, 10) : Number(g);
              return (num === 1 || num === 2) ? num : null;
            }).filter(g => g !== null);
          }
        } catch (e) {
          gendersArray = [];
        }
      }
    }
    
    // Get ad set name
    const adSetName = getFieldValue(fields, 'ad_set.name', `Ad Set ${index + 1}`);
    
    // Get start and end dates
    const startDate = getFieldValue(fields, 'ad_set.start_date');
    const endDate = getFieldValue(fields, 'ad_set.end_date');
    const pixelId = adSet.pixel_id ?? getFieldValue(fields, 'ad_set.pixel_id');
    const customEventType = adSet.custom_event_type ?? getFieldValue(fields, 'ad_set.custom_event_type');
    
    const ageMinVal = getFieldValue(fields, 'ad_set.age_min') ?? adSet.age_min;
    const ageMaxVal = getFieldValue(fields, 'ad_set.age_max') ?? adSet.age_max;
    setEditableValues(prev => {
      if (prev.destinationType !== destinationType ||
          prev.billingEvent !== billingEvent ||
          prev.optimizationGoal !== optimizationGoal ||
          prev.bidStrategy !== bidStrategy ||
          prev.name !== adSetName ||
          prev.ageMin !== (ageMinVal != null ? Number(ageMinVal) : '') ||
          prev.ageMax !== (ageMaxVal != null ? Number(ageMaxVal) : '') ||
          prev.startDate !== formatDateInEventTz(startDate, eventTimezone) ||
          prev.endDate !== formatDateInEventTz(endDate, eventTimezone) ||
          prev.pixelId !== (pixelId || '') ||
          prev.customEventType !== (customEventType || '') ||
          JSON.stringify(prev.publisherPlatforms) !== JSON.stringify(publisherPlatformsArray) ||
          JSON.stringify(prev.devicePlatforms) !== JSON.stringify(devicePlatformsArray) ||
          JSON.stringify(prev.genders) !== JSON.stringify(gendersArray)) {
        return {
          ...prev,
          destinationType,
          publisherPlatforms: publisherPlatformsArray,
          devicePlatforms: devicePlatformsArray,
          billingEvent,
          optimizationGoal,
          bidStrategy,
          genders: gendersArray,
          name: adSetName,
          ageMin: ageMinVal != null ? Number(ageMinVal) : '',
          ageMax: ageMaxVal != null ? Number(ageMaxVal) : '',
          startDate: formatDateInEventTz(startDate, eventTimezone),
          endDate: formatDateInEventTz(endDate, eventTimezone),
          pixelId: pixelId || '',
          customEventType: customEventType || ''
        };
      }
      return prev;
    });
  }, [fields, adSet.pixel_id, adSet.custom_event_type, adSet.age_min, adSet.age_max, eventTimezone]);

  // Validate schedule is in future (event timezone), same as Edit Post modal
  const scheduleValidation = useMemo(
    () => validateScheduleInFuture(editableValues.startDate, editableValues.endDate, eventTimezone),
    [editableValues.startDate, editableValues.endDate, eventTimezone]
  );

  const name = getFieldValue(fields, 'ad_set.name', `Ad Set ${index + 1}`);
  const role = getFieldValue(fields, 'ad_set.role', 'prospecting');
  const ageMin = getFieldValue(fields, 'ad_set.age_min');
  const ageMax = getFieldValue(fields, 'ad_set.age_max');
  const radiusFromFields = getFieldValue(fields, 'ad_set.geo.radius_miles');
  const targeting = adSet.targeting || {};
  const radius = targeting.radius != null ? targeting.radius : radiusFromFields;
  const hasPreciseLocation = !!(targeting.facebook_location && (targeting.radius != null || targeting.radius_miles != null));
  const interests = getFieldValue(fields, 'ad_set.detailed_targeting.interests');

  const campaignObjective = (campaign?.objective || getFieldValue(fullResponse?.campaign?.fields || [], 'campaign.objective') || '').toUpperCase();
  // Show pixel fields for OUTCOME_SALES (required) and OUTCOME_TRAFFIC (optional)
  const showPixelOption = campaignObjective === 'OUTCOME_SALES' || campaignObjective === 'OUTCOME_TRAFFIC';
  const flow = getObjectiveFlow(campaignObjective);

  // When campaign objective (flow) changes, reset ad set billing event and optimization goal to valid defaults if current values are invalid
  useEffect(() => {
    if (!flow) return;
    setEditableValues(prev => {
      const validBilling = flow.billingEvents.includes(prev.billingEvent);
      const validOptimization = flow.optimizationGoals.includes(prev.optimizationGoal);
      if (validBilling && validOptimization) return prev;
      return {
        ...prev,
        ...(!validBilling && { billingEvent: flow.billingEvents[0] }),
        ...(!validOptimization && { optimizationGoal: flow.optimizationGoals[0] })
      };
    });
  }, [campaignObjective]);

  const roleLabels = {
    prospecting: 'Prospecting',
    retargeting: 'Retargeting',
    regional_traveler: 'Regional',
  };

  return (
    <div className={`cr-adset ${isExpanded ? 'cr-adset--expanded' : ''}`}>
      <button className="cr-adset__header" onClick={onToggle}>
        <div className="cr-adset__header-left">
          <span className={`cr-adset__role cr-adset__role--${role}`}>
            {roleLabels[role] || role}
          </span>
          <h4 className="cr-adset__name">{safeRender(name)}</h4>
        </div>
        <div className="cr-adset__header-right">
          <span className="cr-adset__ads-count">{ads.length} ads</span>
          <svg
            className="cr-adset__chevron"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="cr-adset__content">
          {!isAdSetEditable && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#FFF4E6',
              border: '1px solid #FFD89B',
              borderRadius: '6px',
              marginBottom: '1rem',
              color: '#8B4513',
              fontSize: '0.875rem'
            }}>
              This ad set has been published. Edits are only available when the ad set is in draft or failed.
            </div>
          )}
          {String(adSet?.status || '').toLowerCase() === 'failed' && adSet?.error_message && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#FFEBEE',
              border: '1px solid #ef5350',
              borderRadius: '6px',
              marginBottom: '1rem',
              color: '#c62828',
              fontSize: '0.875rem'
            }}>
              <strong>Ad set Error:</strong> {adSet.error_message}
            </div>
          )}
          {/* Name - same section style as Targeting */}
          <div className="cr-adset__targeting">
            <h5 className="cr-adset__section-title">Name</h5>
            <div className="cr-adset__targeting-grid">
              <div className="cr-field">
                <div className="cr-field__header">
                  <span className="cr-field__label-wrap">
                    <span className="cr-field__label">Ad Set Name</span>
                    {(getField(fields, 'ad_set.name')?.explanation) && (
                      <InfoWithTooltip text={getField(fields, 'ad_set.name')?.explanation} />
                    )}
                  </span>
                </div>
                <div className="cr-field__value">
                  <input
                    type="text"
                    value={editableValues.name || ''}
                    onChange={(e) => setEditableValues(prev => ({ ...prev, name: e.target.value }))}
                    disabled={!isAdSetEditable}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E5E5E5',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                      cursor: isAdSetEditable ? 'text' : 'not-allowed',
                      opacity: isAdSetEditable ? 1 : 0.8,
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter ad set name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div className="cr-adset__targeting">
            <h5 className="cr-adset__section-title">Targeting</h5>
            <div className="cr-adset__targeting-grid">
              {isAdSetEditable ? (
                <div className="cr-field">
                  <div className="cr-field__header">
                    <span className="cr-field__label-wrap">
                      <span className="cr-field__label">Age Range</span>
                      {(getField(fields, 'ad_set.age_min')?.explanation || getField(fields, 'ad_set.age_max')?.explanation) && (
                        <InfoWithTooltip text={getField(fields, 'ad_set.age_min')?.explanation || getField(fields, 'ad_set.age_max')?.explanation} />
                      )}
                    </span>
                  </div>
                  <div className="cr-field__value" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.8rem', color: '#666' }}>Min</label>
                      <input
                        type="number"
                        min={18}
                        max={65}
                        value={editableValues.ageMin === '' ? '' : editableValues.ageMin}
                        onChange={(e) => setEditableValues(prev => ({ ...prev, ageMin: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))}
                        style={{
                          width: '4rem',
                          padding: '0.35rem 0.5rem',
                          border: '1px solid #E5E5E5',
                          borderRadius: '4px',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.8rem', color: '#666' }}>Max</label>
                      <input
                        type="number"
                        min={18}
                        max={65}
                        value={editableValues.ageMax === '' ? '' : editableValues.ageMax}
                        onChange={(e) => setEditableValues(prev => ({ ...prev, ageMax: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))}
                        style={{
                          width: '4rem',
                          padding: '0.35rem 0.5rem',
                          border: '1px solid #E5E5E5',
                          borderRadius: '4px',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>years</span>
                  </div>
                </div>
              ) : (
                <FieldRow
                  label="Age Range"
                  value={ageMin && ageMax ? `${ageMin}–${ageMax} years` : null}
                  summary={getField(fields, 'ad_set.age_min')?.explanation_summary}
                  explanation={getField(fields, 'ad_set.age_min')?.explanation}
                  confidence={getField(fields, 'ad_set.age_min')?.confidence}
                />
              )}
              {/* Interest Targeting */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Interests</label>
                  {getField(fields, 'ad_set.detailed_targeting.interests')?.explanation && (
                    <InfoWithTooltip text={getField(fields, 'ad_set.detailed_targeting.interests')?.explanation} />
                  )}
                </span>
                <InterestTargetingInput
                  interests={editableValues.interests}
                  onChange={(updated) => setEditableValues(prev => ({ ...prev, interests: updated }))}
                  disabled={!isAdSetEditable}
                  companySlug={campaign?.company_slug}
                />
              </div>

              <FacebookLocationRow
                facebookLocation={targeting.facebook_location || null}
                radius={radius}
                hasPreciseLocation={hasPreciseLocation}
                currentLatitude={targeting.geo_locations?.custom_locations?.[0]?.latitude ?? null}
                currentLongitude={targeting.geo_locations?.custom_locations?.[0]?.longitude ?? null}
                campaignId={campaign?.id}
                adSetId={adSet.id}
                onRefetchCampaign={onRefetchCampaign}
                locationExplanation={getField(fields, 'ad_set.geo.location_name')?.explanation || getField(fields, 'ad_set.geo_locations')?.explanation || getField(fields, 'ad_set.detailed_targeting.geo_locations')?.explanation || (adSet.analysis_metadata?.ad_set_data?.location?.reason) || 'Location and radius define the geographic area where your ads can be shown.'}
                scopeUrl={scopeUrl}
              />
            </div>
          </div>

          {/* Additional Ad Set Fields */}
          <div className="cr-adset__additional-fields">
            <h5 className="cr-adset__section-title">Additional Settings</h5>
            <div className="cr-adset__targeting-grid" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Destination Type - Editable */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Destination Type
                  <InfoWithTooltip text={getField(fields, 'ad_set.destination_type')?.explanation || 'Where people go when they click your ad (e.g. website, app, Messenger).'} />
                </label>
                <select
                  value={editableValues.destinationType}
                  onChange={(e) => setEditableValues(prev => ({ ...prev, destinationType: e.target.value }))}
                  disabled={!isAdSetEditable}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                    cursor: isAdSetEditable ? 'pointer' : 'not-allowed',
                    opacity: isAdSetEditable ? 1 : 0.8
                  }}
                >
                  <option value="">Select Destination Type</option>
                  <option value="WEBSITE">Website</option>
                  <option value="APP">App</option>
                  <option value="MESSENGER">Messenger</option>
                  <option value="INSTAGRAM_DIRECT">Instagram Direct</option>
                </select>
              </div>
              {/* Publisher Platforms - Editable (multi-select) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Publisher Platforms
                  <InfoWithTooltip text={getField(fields, 'ad_set.publisher_platforms')?.explanation || 'Where your ads can appear (Facebook, Instagram, or both).'} />
                </label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {['facebook', 'instagram'].map(platform => (
                    <label key={platform} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isAdSetEditable ? 'pointer' : 'not-allowed', opacity: isAdSetEditable ? 1 : 0.8 }}>
                      <input
                        type="checkbox"
                        checked={editableValues.publisherPlatforms.includes(platform)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditableValues(prev => ({
                              ...prev,
                              publisherPlatforms: [...prev.publisherPlatforms, platform]
                            }));
                          } else {
                            setEditableValues(prev => ({
                              ...prev,
                              publisherPlatforms: prev.publisherPlatforms.filter(p => p !== platform)
                            }));
                          }
                        }}
                        disabled={!isAdSetEditable}
                        style={{ cursor: isAdSetEditable ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ fontSize: '0.875rem', textTransform: 'capitalize' }}>{platform}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Device Placements - Editable (multi-select) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Device Placements
                  <InfoWithTooltip text={getField(fields, 'ad_set.device_placements')?.explanation || 'Device types where your ads can show (mobile, desktop).'} />
                </label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {['mobile', 'desktop'].map(device => (
                    <label key={device} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isAdSetEditable ? 'pointer' : 'not-allowed', opacity: isAdSetEditable ? 1 : 0.8 }}>
                      <input
                        type="checkbox"
                        checked={editableValues.devicePlatforms.includes(device)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditableValues(prev => ({
                              ...prev,
                              devicePlatforms: [...prev.devicePlatforms, device]
                            }));
                          } else {
                            setEditableValues(prev => ({
                              ...prev,
                              devicePlatforms: prev.devicePlatforms.filter(d => d !== device)
                            }));
                          }
                        }}
                        disabled={!isAdSetEditable}
                        style={{ cursor: isAdSetEditable ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ fontSize: '0.875rem', textTransform: 'capitalize' }}>{device}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Billing Event - Editable (valid options per campaign objective flow) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Billing Event
                  <InfoWithTooltip text={getField(fields, 'ad_set.billing_event')?.explanation || 'What you pay for (e.g. impressions, link clicks).'} />
                </label>
                <select
                  value={flow?.billingEvents?.includes(editableValues.billingEvent) ? editableValues.billingEvent : (flow?.billingEvents?.[0] || editableValues.billingEvent)}
                  onChange={(e) => setEditableValues(prev => ({ ...prev, billingEvent: e.target.value }))}
                  disabled={!isAdSetEditable}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                    cursor: isAdSetEditable ? 'pointer' : 'not-allowed',
                    opacity: isAdSetEditable ? 1 : 0.8
                  }}
                >
                  {(flow?.billingEvents || ['IMPRESSIONS']).map(ev => (
                    <option key={ev} value={ev}>{ev.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Optimization Goal - Editable (valid options per campaign objective flow) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Optimization Goal
                  <InfoWithTooltip text={getField(fields, 'ad_set.optimization_goal')?.explanation || 'What Meta optimizes for when delivering your ads (e.g. link clicks, conversions).'} />
                </label>
                <select
                  value={flow?.optimizationGoals?.includes(editableValues.optimizationGoal) ? editableValues.optimizationGoal : (flow?.optimizationGoals?.[0] || editableValues.optimizationGoal)}
                  onChange={(e) => setEditableValues(prev => ({ ...prev, optimizationGoal: e.target.value }))}
                  disabled={!isAdSetEditable}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                    cursor: isAdSetEditable ? 'pointer' : 'not-allowed',
                    opacity: isAdSetEditable ? 1 : 0.8
                  }}
                >
                  {(flow?.optimizationGoals || ['LINK_CLICKS']).map(goal => (
                    <option key={goal} value={goal}>{goal.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Bid Strategy - Editable */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Bid Strategy
                  <InfoWithTooltip text={getField(fields, 'ad_set.bid_strategy')?.explanation || 'How Meta bids in the auction (e.g. lowest cost without cap).'} />
                </label>
                <select
                  value={editableValues.bidStrategy}
                  onChange={(e) => setEditableValues(prev => ({ ...prev, bidStrategy: e.target.value }))}
                  disabled={!isAdSetEditable}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                    cursor: isAdSetEditable ? 'pointer' : 'not-allowed',
                    opacity: isAdSetEditable ? 1 : 0.8
                  }}
                >
                  <option value="LOWEST_COST_WITHOUT_CAP">Lowest Cost (Highest Volume)</option>
                  <option value="COST_CAP">Cost Cap</option>
                  <option value="LOWEST_COST_WITH_BID_CAP">Bid Cap</option>
                  <option value="LOWEST_COST_WITH_MIN_ROAS">Minimum ROAS</option>
                </select>
                {(editableValues.bidStrategy === 'COST_CAP' || editableValues.bidStrategy === 'LOWEST_COST_WITH_BID_CAP') && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                      {editableValues.bidStrategy === 'COST_CAP' ? 'Cost cap amount ($)' : 'Bid cap amount ($)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editableValues.bidAmount || ''}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, bidAmount: e.target.value }))}
                      disabled={!isAdSetEditable}
                      placeholder="e.g. 5.00"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5'
                      }}
                    />
                  </div>
                )}
                {editableValues.bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Minimum ROAS target</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editableValues.bidAmount || ''}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, bidAmount: e.target.value }))}
                      disabled={!isAdSetEditable}
                      placeholder="e.g. 2.00"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Genders - Editable (multi-select) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>
                  Genders
                  <InfoWithTooltip text={getField(fields, 'ad_set.genders')?.explanation || 'Gender targeting for your audience (all, male only, female only).'} />
                </label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {[
                    { value: 1, label: 'Male only' },
                    { value: 2, label: 'Female only' }
                  ].map(gender => {
                    // Logic:
                    // - If genders = [] or [1, 2] → both checkboxes checked
                    // - If genders = [1] → only Male checked
                    // - If genders = [2] → only Female checked
                    // Normalize genders to numbers for comparison
                    const normalizedGenders = editableValues.genders.map(g => Number(g));
                    const hasBoth = normalizedGenders.length === 0 || 
                                   (normalizedGenders.includes(1) && normalizedGenders.includes(2));
                    // For each checkbox: if both are selected OR this specific value is in the array
                    const isChecked = hasBoth || normalizedGenders.includes(Number(gender.value));
                    
                    return (
                      <label key={gender.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isAdSetEditable ? 'pointer' : 'not-allowed', opacity: isAdSetEditable ? 1 : 0.8 }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={!isAdSetEditable}
                          onChange={(e) => {
                            let newGenders;
                            // Normalize for comparison
                            const normalizedGenders = editableValues.genders.map(g => Number(g));
                            const currentlyHasBoth = normalizedGenders.length === 0 || 
                                                    (normalizedGenders.includes(1) && normalizedGenders.includes(2));
                            
                            if (e.target.checked) {
                              // Checking a checkbox
                              if (currentlyHasBoth) {
                                // Both are currently checked (empty or [1,2]), checking one means only that one
                                newGenders = [gender.value];
                              } else {
                                // One is selected, adding the other means both = [1, 2]
                                newGenders = [...editableValues.genders, gender.value];
                                if (newGenders.length === 2) {
                                  newGenders = [1, 2]; // Both selected = [1, 2]
                                }
                              }
                            } else {
                              // Unchecking a checkbox
                              if (currentlyHasBoth) {
                                // Both are checked, unchecking one means only the other
                                newGenders = gender.value === 1 ? [2] : [1];
                              } else {
                                // One is selected, unchecking it means empty (all)
                                newGenders = editableValues.genders.filter(g => g !== gender.value);
                                if (newGenders.length === 0) {
                                  newGenders = []; // Empty = all selected
                                }
                              }
                            }
                            setEditableValues(prev => ({
                              ...prev,
                              genders: newGenders
                            }));
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.875rem' }}>{gender.label}</span>
                      </label>
                    );
                  })}
                </div>
                {(editableValues.genders.length === 0 || 
                  (editableValues.genders.includes(1) && editableValues.genders.includes(2))) && (
                  <span style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic' }}>All genders selected</span>
                )}
              </div>
              {/* Adset Budget - Show CBO if campaign has budget, otherwise show adset budget */}
              {(() => {
                // Get campaign budget from fullResponse (same way as OverviewPanel)
                const campaignFields = fullResponse?.campaign?.fields || [];
                const campaignBudgetTotal = getFieldValue(campaignFields, 'campaign.budget_amount_total');
                const campaignBudgetDaily = getFieldValue(campaignFields, 'campaign.budget_amount_daily');
                const campaignHasBudget = campaignBudgetTotal || campaignBudgetDaily;
                
                if (campaignHasBudget) {
                  const budgetField = campaignBudgetTotal
                    ? getField(campaignFields, 'campaign.budget_amount_total')
                    : getField(campaignFields, 'campaign.budget_amount_daily');
                  const budgetDisplay = campaignBudgetTotal
                    ? `Budget managed at campaign level (CBO) — ${formatCurrency(campaignBudgetTotal)} lifetime`
                    : campaignBudgetDaily
                      ? `Budget managed at campaign level (CBO) — ${formatCurrency(campaignBudgetDaily)}/day`
                      : 'Budget managed at campaign level (CBO)';
                  return (
                    <>
                      <FieldRow
                        label="Adset Budget"
                        value={budgetDisplay}
                        summary={budgetField?.explanation_summary || 'Budget is managed at the campaign level'}
                        explanation={budgetField?.explanation || 'This campaign uses Campaign Budget Optimization (CBO), which means the budget is set at the campaign level and distributed across ad sets automatically.'}
                        confidence={budgetField?.confidence || 'high'}
                      />
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', color: '#666', fontStyle: 'italic' }}>
                        Edit budget in the Overview tab
                      </p>
                    </>
                  );
                } else {
                  const dailyBudget = getFieldValue(fields, 'ad_set.budget_amount_daily');
                  const lifetimeBudget = getFieldValue(fields, 'ad_set.budget_amount_total');
                  if (dailyBudget || lifetimeBudget) {
                    return (
                      <FieldRow
                        label="Adset Budget"
                        value={dailyBudget ? `$${(dailyBudget / 100).toFixed(2)}/day` : `$${(lifetimeBudget / 100).toFixed(2)} total`}
                        summary={getField(fields, dailyBudget ? 'ad_set.budget_amount_daily' : 'ad_set.budget_amount_total')?.explanation_summary}
                        explanation={getField(fields, dailyBudget ? 'ad_set.budget_amount_daily' : 'ad_set.budget_amount_total')?.explanation}
                        confidence={getField(fields, dailyBudget ? 'ad_set.budget_amount_daily' : 'ad_set.budget_amount_total')?.confidence}
                      />
                    );
                  }
                }
                return null;
              })()}
              {/* Schedule (start and end time) - Editable */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Schedule</label>
                  {(() => {
                    const startReason = getField(fields, 'ad_set.start_date')?.explanation;
                    const endReason = getField(fields, 'ad_set.end_date')?.explanation;
                    const scheduleReason = (startReason && endReason) ? `Start: ${startReason}\n\nEnd: ${endReason}` : (startReason || endReason || '');
                    return scheduleReason ? <InfoWithTooltip text={scheduleReason} /> : null;
                  })()}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Start Date & Time</label>
                    <input
                      type="datetime-local"
                      value={editableValues.startDate || ''}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, startDate: e.target.value }))}
                      min={isAdSetEditable ? nowInEventTzForInput(eventTimezone) : undefined}
                      disabled={!isAdSetEditable}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                        cursor: isAdSetEditable ? 'text' : 'not-allowed',
                        opacity: isAdSetEditable ? 1 : 0.8
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>End Date & Time</label>
                    <input
                      type="datetime-local"
                      value={editableValues.endDate || ''}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, endDate: e.target.value }))}
                      min={editableValues.startDate || (isAdSetEditable ? nowInEventTzForInput(eventTimezone) : '')}
                      disabled={!isAdSetEditable}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                        cursor: isAdSetEditable ? 'text' : 'not-allowed',
                        opacity: isAdSetEditable ? 1 : 0.8
                      }}
                    />
                  </div>
                </div>
                {isAdSetEditable && (editableValues.startDate || editableValues.endDate) && !scheduleValidation.valid && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#c62828' }}>
                    {scheduleValidation.message}
                  </p>
                )}
                {isAdSetEditable && (editableValues.startDate || editableValues.endDate) && scheduleValidation.valid && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#0a7c42' }}>
                    Schedule is in the future (event timezone).
                  </p>
                )}
              </div>
              {/* Conversion pixel – shown when objective is OUTCOME_SALES */}
              {showPixelOption && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Conversion pixel</span>
                        <InfoWithTooltip text="A Meta Pixel is code on your website or app that tracks actions people take (purchases, sign-ups, add to cart, etc.). Linking a pixel here lets Meta: (1) optimize ad delivery to people likely to convert, (2) measure conversions and ROAS, and (3) build audiences for retargeting. If no pixel appears in the list, click Sync pixels to pull in pixels from your connected ad accounts." />
                      </span>
                      {onSyncPixels && (
                        <button
                          type="button"
                          className="cr-btn cr-btn--primary"
                          onClick={onSyncPixels}
                          disabled={syncPixelsLoading || pixelsLoading}
                          style={{
                            padding: '0.35rem 0.65rem',
                            fontSize: '0.8rem',
                            cursor: syncPixelsLoading || pixelsLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {syncPixelsLoading ? 'Syncing…' : 'Sync pixels'}
                        </button>
                      )}
                    </div>
                    <select
                      value={editableValues.pixelId || ''}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, pixelId: e.target.value }))}
                      disabled={!isAdSetEditable}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                        cursor: isAdSetEditable ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <option value="">No pixel</option>
                      {pixels.map(p => (
                        <option key={p.id} value={p.pixel_id}>{p.name || `Pixel ${p.pixel_id}`}</option>
                      ))}
                    </select>
                  </div>
                  {/* Conversion event type – valid options per OUTCOME_SALES flow */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Conversion event type</label>
                    <select
                      value={flow?.eventTypes?.includes(editableValues.customEventType) ? editableValues.customEventType : (flow?.eventTypes?.[0] || 'PURCHASE')}
                      onChange={(e) => setEditableValues(prev => ({ ...prev, customEventType: e.target.value }))}
                      disabled={!isAdSetEditable}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        backgroundColor: isAdSetEditable ? '#fff' : '#F5F5F5',
                        cursor: isAdSetEditable ? 'pointer' : 'not-allowed',
                        color: isAdSetEditable ? '#1F1F1F' : '#666'
                      }}
                    >
                      {(flow?.eventTypes || ['PURCHASE']).map(ev => (
                        <option key={ev} value={ev}>{ev.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            
            {/* Save Changes Button - only when ad set is draft or failed (same UI as Creative tab) */}
            {firstAdId && isAdSetEditable && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E5E5', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  className="cr-btn cr-btn--primary"
                  style={{ width: '100%' }}
                  onClick={async () => {
                    if (!firstAdId) return;
                    if (!scheduleValidation.valid) {
                      onSaveAdSet('error', scheduleValidation.message || 'Start and end date must be in the future (event timezone).');
                      return;
                    }
                    setSaving(true);
                    try {
                      const adSetData = {
                        name: editableValues.name && editableValues.name.trim() ? editableValues.name.trim() : null,
                        destination_type: editableValues.destinationType || null,
                        publisher_platforms: editableValues.publisherPlatforms.length > 0 ? editableValues.publisherPlatforms : null,
                        device_placements: editableValues.devicePlatforms.length > 0 ? editableValues.devicePlatforms : null,
                        billing_event: editableValues.billingEvent || null,
                        optimization_goal: editableValues.optimizationGoal || null,
                        bid_strategy: editableValues.bidStrategy || null,
                        genders: (() => {
                          const hasBoth = editableValues.genders.length === 0 ||
                                         (editableValues.genders.includes(1) && editableValues.genders.includes(2));
                          return hasBoth ? [] : editableValues.genders;
                        })(),
                        age_min: editableValues.ageMin !== '' && editableValues.ageMin != null ? parseInt(editableValues.ageMin, 10) : null,
                        age_max: editableValues.ageMax !== '' && editableValues.ageMax != null ? parseInt(editableValues.ageMax, 10) : null,
                        start_date: eventTzDatetimeToUTC(editableValues.startDate, eventTimezone),
                        end_date: eventTzDatetimeToUTC(editableValues.endDate, eventTimezone),
                        pixel_id: editableValues.pixelId || null,
                        custom_event_type: editableValues.pixelId ? (editableValues.customEventType || 'PURCHASE') : null,
                        interests: editableValues.interests.length > 0 ? editableValues.interests : null
                      };
                      
                      const response = await fetch(`/facebook_ads/${firstAdId}`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                        },
                        body: JSON.stringify({
                          ad_set: adSetData
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                      }
                      
                      const data = await response.json();
                      
                      if (data.success) {
                        // Update local display so start/end show saved values in event timezone
                        const updatedSet = data.ad?.facebook_ad_set;
                        if (updatedSet && (updatedSet.start_date != null || updatedSet.end_date != null)) {
                          setEditableValues(prev => ({
                            ...prev,
                            ...(updatedSet.start_date != null && { startDate: formatDateInEventTz(updatedSet.start_date, eventTimezone) }),
                            ...(updatedSet.end_date != null && { endDate: formatDateInEventTz(updatedSet.end_date, eventTimezone) })
                          }));
                        }
                        if (onSaveAdSet) {
                          onSaveAdSet('success', 'Ad set updated successfully');
                        } else {
                          alert('Ad set updated successfully');
                        }
                      } else {
                        throw new Error(data.error || 'Failed to update ad set');
                      }
                    } catch (error) {
                      console.error('Error updating ad set:', error);
                      if (onSaveAdSet) {
                        onSaveAdSet('error', error.message || 'Failed to update ad set');
                      } else {
                        alert(`Error: ${error.message || 'Failed to update ad set'}`);
                      }
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving || !isAdSetEditable}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AD CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const AdCard = ({ ad, index, showVariations = true, ageRange = null }) => {
  const [isExpanded, setIsExpanded] = useState(index === 0);
  const fields = ad.fields || [];

  const name = getFieldValue(fields, 'ad.name', `Ad ${index + 1}`);
  const cta = getFieldValue(fields, 'ad.call_to_action', 'get_tickets');
  const url = getFieldValue(fields, 'ad.destination_url');
  const notes = getFieldValue(fields, 'ad.notes');

  const headlines = getFieldValue(fields, 'ad.copy.headline_variations', []);
  const primaryTexts = getFieldValue(fields, 'ad.copy.primary_text_variations', []);
  const descriptions = getFieldValue(fields, 'ad.copy.description_variations', []);

  return (
    <div className={`cr-ad ${isExpanded ? 'cr-ad--expanded' : ''}`}>
      <button className="cr-ad__header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="cr-ad__header-left">
          <span className="cr-ad__index">{index + 1}</span>
          <span className="cr-ad__name">{safeRender(name)}</span>
        </div>
        <svg
          className="cr-ad__chevron"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path d="M3.5 5.25l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isExpanded && (
        <div className="cr-ad__content">
          {notes && (
            <p className="cr-ad__notes">{safeRender(notes)}</p>
          )}

          {showVariations ? (
            <>
              <div className="cr-ad__copy">
                <CopyVariations
                  label="Headlines"
                  variations={Array.isArray(headlines) ? headlines : []}
                  explanation={getField(fields, 'ad.copy.headline_variations')?.explanation || getField(fields, 'ad.copy.headline_variations')?.explanation_summary}
                />
                <CopyVariations
                  label="Primary Text"
                  variations={Array.isArray(primaryTexts) ? primaryTexts : []}
                  explanation={getField(fields, 'ad.copy.primary_text_variations')?.explanation || getField(fields, 'ad.copy.primary_text_variations')?.explanation_summary}
                />
                {Array.isArray(descriptions) && descriptions.length > 0 && (
                  <CopyVariations
                    label="Description"
                    variations={descriptions}
                    explanation={getField(fields, 'ad.copy.description_variations')?.explanation || getField(fields, 'ad.copy.description_variations')?.explanation_summary}
                  />
                )}
              </div>

              <div className="cr-ad__meta">
                <FieldRow
                  label="CTA Button"
                  value={CTA_LABELS[cta] || cta}
                  summary={getField(fields, 'ad.call_to_action')?.explanation_summary}
                  explanation={getField(fields, 'ad.call_to_action')?.explanation}
                />
                <FieldRow
                  label="Destination"
                  value={url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="cr-ad__url">
                      {(() => { try { return new URL(url).hostname; } catch { return url; } })()}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M9 6.5v3a.5.5 0 01-.5.5h-6a.5.5 0 01-.5-.5v-6a.5.5 0 01.5-.5h3M7 2h3v3M5 7l5-5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  ) : null}
                  summary={getField(fields, 'ad.destination_url')?.explanation_summary}
                  explanation={getField(fields, 'ad.destination_url')?.explanation}
                />
              </div>
            </>
          ) : (
            <div className="cr-ad__simple" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div className="cr-field">
                <div className="cr-field__header">
                  <span className="cr-field__label-wrap">
                    <span className="cr-field__label">Ad Creative Name</span>
                    {(getField(fields, 'ad.creative.name')?.explanation) && (
                      <InfoWithTooltip text={getField(fields, 'ad.creative.name')?.explanation} />
                    )}
                  </span>
                </div>
                <div className="cr-field__value">
                  {safeRender(name)}
                </div>
              </div>
              {ageRange && (
                <div className="cr-field">
                  <div className="cr-field__header">
                    <span className="cr-field__label">Age Range</span>
                  </div>
                  <div className="cr-field__value">
                    {ageRange}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SOURCES SECTION
// ═══════════════════════════════════════════════════════════════════════════

const SourcesSection = ({ sources }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="cr-sources">
      <button
        className="cr-sources__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h4 className="cr-sources__title">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 4h12M2 8h12M2 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {sources.length} Sources Cited
        </h4>
        <svg
          className={`cr-sources__chevron ${isExpanded ? 'cr-sources__chevron--open' : ''}`}
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path d="M3.5 5.25l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isExpanded && (
        <div className="cr-sources__list">
          {sources.map((source, i) => (
            <div key={source.id || i} className="cr-sources__item">
              <span className="cr-sources__id">[{source.id}]</span>
              <div className="cr-sources__content">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cr-sources__link"
                >
                  {source.title}
                </a>
                {source.notes && (
                  <p className="cr-sources__notes">{source.notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Merge linked ad account into the list when it is archived (not returned by ad_accounts_status).
function mergeLinkedArchivedAdAccount(baseList, campaign) {
  const list = Array.isArray(baseList) ? [...baseList] : [];
  const cid = campaign?.facebook_ad_account_id;
  if (!cid) return list;
  if (list.some((a) => Number(a.id) === Number(cid))) return list;

  const nameBase =
    (campaign.ad_account_name && String(campaign.ad_account_name).trim()) ||
    (campaign.ad_account_id && String(campaign.ad_account_id)) ||
    `Ad account #${cid}`;
  const labelName = campaign.ad_account_archived === false ? nameBase : `${nameBase} (Archived)`;
  list.push({
    id: cid,
    ad_account_id: campaign.ad_account_id || '',
    name: labelName,
    account_status: '',
    currency: '',
    selected: false,
    _mergedArchived: true
  });
  return list;
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB PANELS
// ═══════════════════════════════════════════════════════════════════════════

const OverviewPanel = ({ campaign, fullResponse, onAdAccountChange, onSave, onRefetchCampaign, scopeUrl }) => {
  const campaignFields = fullResponse?.campaign?.fields || [];
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccountId, setSelectedAdAccountId] = useState(campaign?.facebook_ad_account_id || null);
  const [loading, setLoading] = useState(false);
  const [editingObjective, setEditingObjective] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(() => {
    const obj = getFieldValue(campaignFields, 'campaign.objective') || campaign?.objective;
    return obj ? obj.toUpperCase() : '';
  });
  const [editingCampaignName, setEditingCampaignName] = useState(false);
  const [campaignNameValue, setCampaignNameValue] = useState(() => {
    return getFieldValue(campaignFields, 'campaign.name') || campaign?.campaign_name || '';
  });
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetStrategy, setBudgetStrategy] = useState(() => {
    const hasLifetime = budgetTotal && budgetTotal > 0;
    const hasDaily = budgetDaily && budgetDaily > 0;
    return hasLifetime ? 'lifetime' : hasDaily ? 'daily' : 'daily';
  });
  const [budgetAmount, setBudgetAmount] = useState(() => {
    return budgetTotal || budgetDaily || '';
  });
  const [savingAll, setSavingAll] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Valid campaign objectives (must match OBJECTIVE_FLOWS). User selects one valid flow.
  const validObjectives = [
    'OUTCOME_AWARENESS',
    'OUTCOME_TRAFFIC',
    'OUTCOME_ENGAGEMENT',
    'OUTCOME_SALES'
  ];
  const objectiveLabel = (value) => {
    if (!value) return '';
    const labels = {
      OUTCOME_AWARENESS: 'Outcome awareness',
      OUTCOME_TRAFFIC: 'Outcome traffic',
      OUTCOME_ENGAGEMENT: 'Outcome engagement',
      OUTCOME_SALES: 'Outcome sales (conversions)'
    };
    const key = String(value).toUpperCase();
    return labels[key] || key.replace(/_/g, ' ');
  };

  const name = getFieldValue(campaignFields, 'campaign.name') || campaign?.campaign_name;
  const objective = getFieldValue(campaignFields, 'campaign.objective') || campaign?.objective;
  const budgetType = getFieldValue(campaignFields, 'campaign.budget_type');
  const budgetTotal = getFieldValue(campaignFields, 'campaign.budget_amount_total');
  const budgetDaily = getFieldValue(campaignFields, 'campaign.budget_amount_daily');
  const analysisMeta = campaign?.analysis_metadata?.campaign_data || {};
  const startAt = campaign?.start_time ?? analysisMeta.campaign_start_at?.value ?? getFieldValue(campaignFields, 'campaign.start_at') ?? campaign?.scheduled_start_at;
  const endAt = campaign?.end_time ?? analysisMeta.campaign_end_at?.value ?? getFieldValue(campaignFields, 'campaign.end_at') ?? campaign?.scheduled_end_at;
  const internalNotes = getFieldValue(campaignFields, 'campaign.internal_notes');

  // Edit/override only when campaign is in draft
  const isCampaignDraft = campaign?.status === 'draft' ;

  // Check if budget should be shown (only if campaign has budget or ad sets have budget)
  const shouldShowBudget = budgetTotal || budgetDaily;

  // Fetch ad accounts; include this campaign's linked account if archived (not in active-only API list)
  useEffect(() => {
    setLoading(true);
    fetch(scopeUrl('/api/facebook/ad_accounts_status'), { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(data => {
        const base = data && Array.isArray(data.ad_accounts) ? data.ad_accounts : [];
        setAdAccounts(mergeLinkedArchivedAdAccount(base, campaign));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [scopeUrl, campaign?.id, campaign?.facebook_ad_account_id, campaign?.ad_account_name, campaign?.ad_account_archived, campaign?.ad_account_id]);

  // Update selected ad account when campaign changes
  useEffect(() => {
    if (campaign?.facebook_ad_account_id) {
      setSelectedAdAccountId(campaign.facebook_ad_account_id);
    }
  }, [campaign?.facebook_ad_account_id]);

  // Update selectedObjective when objective changes
  useEffect(() => {
    const obj = getFieldValue(campaignFields, 'campaign.objective') || campaign?.objective;
    if (obj) {
      setSelectedObjective(obj.toUpperCase());
    }
  }, [campaignFields, campaign?.objective]);

  // Update campaign name when it changes
  useEffect(() => {
    const newName = getFieldValue(campaignFields, 'campaign.name') || campaign?.campaign_name || '';
    setCampaignNameValue(newName);
  }, [campaignFields, campaign?.campaign_name]);

  // Update budget when it changes
  useEffect(() => {
    const hasLifetime = budgetTotal && budgetTotal > 0;
    const hasDaily = budgetDaily && budgetDaily > 0;
    setBudgetStrategy(hasLifetime ? 'lifetime' : hasDaily ? 'daily' : 'daily');
    setBudgetAmount(budgetTotal || budgetDaily || '');
  }, [budgetTotal, budgetDaily]);

  const handleAdAccountChange = (e) => {
    const newAdAccountId = parseInt(e.target.value);
    setSelectedAdAccountId(newAdAccountId);
    if (onAdAccountChange) {
      onAdAccountChange(newAdAccountId);
    }
    // Update campaign object directly so it's included in save
    if (campaign) {
      campaign.facebook_ad_account_id = newAdAccountId;
    }
  };

  const selectedAdAccount = adAccounts.find(acc => acc.id === selectedAdAccountId);

  const hasPendingEdits = (editingCampaignName && (campaignNameValue || '').trim() !== (name || '').trim()) ||
    (editingObjective && selectedObjective !== (objective || '').toUpperCase()) ||
    (editingBudget && (
      (budgetStrategy === 'daily' && budgetAmount !== budgetDaily) ||
      (budgetStrategy === 'lifetime' && budgetAmount !== budgetTotal)
    ));

  const handleSaveAll = async () => {
    if (!campaign || !onSave || savingAll) return;
    setSavingAll(true);
    try {
      let updated = { ...campaign };
      if (editingCampaignName && (campaignNameValue || '').trim()) {
        const response = await fetch(`/api/facebook/campaigns/${campaign.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({ name: campaignNameValue.trim() })
        });
        if (!response.ok) throw new Error('Failed to update campaign name');
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to update campaign name');
        updated = { ...updated, name: campaignNameValue.trim() };
        setEditingCampaignName(false);
      }
      if (editingObjective && selectedObjective) {
        const response = await fetch(`/api/facebook/campaigns/${campaign.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({ objective: selectedObjective })
        });
        if (!response.ok) throw new Error('Failed to update objective');
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to update objective');
        updated = { ...updated, objective: selectedObjective };
        setEditingObjective(false);
      }
      if (editingBudget && budgetAmount && budgetAmount > 0) {
        const updateData = budgetStrategy === 'daily'
          ? { daily_budget: budgetAmount, lifetime_budget: null }
          : { lifetime_budget: budgetAmount, daily_budget: null };
        const response = await fetch(`/api/facebook/campaigns/${campaign.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify(updateData)
        });
        if (!response.ok) throw new Error('Failed to update budget');
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to update budget');
        updated = { ...updated, ...updateData };
        setEditingBudget(false);
      }
      onSave(updated, 'update_campaign');
      onRefetchCampaign?.();
    } catch (error) {
      console.error('Overview save error:', error);
      alert(error.message || 'Failed to save changes');
    } finally {
      setSavingAll(false);
    }
  };

  const isCampaignFailed = String(campaign?.status || '').toLowerCase() === 'failed';
  const showCampaignError = isCampaignFailed && campaign?.error_message;

  return (
    <div className="cr-panel cr-panel--overview">
      {!isCampaignDraft && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FFF4E6',
          border: '1px solid #FFD89B',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#8B4513',
          fontSize: '0.875rem'
        }}>
          This campaign has been published. Edits are only available when the campaign is in draft or failed.
        </div>
      )}
      {showCampaignError && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FFEBEE',
          border: '1px solid #ef5350',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#c62828',
          fontSize: '0.875rem'
        }}>
          <strong>Campaign Error:</strong> {campaign.error_message}
        </div>
      )}
      {/* Campaign Settings */}
      <div className="cr-panel__section">
        <h3 className="cr-panel__section-title">Campaign Settings</h3>
        <div className="cr-panel__fields">
          <div className="cr-field">
            <div className="cr-field__header">
              <span className="cr-field__label">Ad Account</span>
            </div>
            <div className="cr-field__value">
              {loading ? (
                <span style={{ color: '#666' }}>Loading ad accounts...</span>
              ) : adAccounts.length > 0 ? (
                <select
                  value={selectedAdAccountId || ''}
                  onChange={handleAdAccountChange}
                  disabled={!isCampaignDraft}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    cursor: isCampaignDraft ? 'pointer' : 'not-allowed',
                    opacity: isCampaignDraft ? 1 : 0.7
                  }}
                >
                  <option value="">Select Ad Account</option>
                  {adAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account._mergedArchived
                        ? account.name
                        : `${account.name} (${account.account_status === '1' ? 'Active' : account.account_status || 'Inactive'})`}
                    </option>
                  ))}
                </select>
              ) : (
                <span style={{ color: '#999' }}>No ad accounts available</span>
              )}
            </div>
          </div>
          {editingCampaignName ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Campaign Name</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  value={campaignNameValue}
                  onChange={(e) => setCampaignNameValue(e.target.value)}
                  disabled={savingAll || !isCampaignDraft}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    backgroundColor: '#fff',
                    cursor: savingAll ? 'not-allowed' : 'text'
                  }}
                />
                <button
                  onClick={() => {
                    setEditingCampaignName(false);
                    const originalName = getFieldValue(campaignFields, 'campaign.name') || campaign?.campaign_name || '';
                    setCampaignNameValue(originalName);
                  }}
                  disabled={savingAll}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#fff',
                    color: '#1F1F1F',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: savingAll ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <FieldRow
                  label="Campaign Name"
                  value={name}
                  summary={getField(campaignFields, 'campaign.name')?.explanation_summary}
                  explanation={getField(campaignFields, 'campaign.name')?.explanation}
                  confidence={getField(campaignFields, 'campaign.name')?.confidence}
                />
              </div>
              {isCampaignDraft && (
                <button
                  onClick={() => setEditingCampaignName(true)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#1A1A19',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    alignSelf: 'flex-start',
                    marginTop: '0.5rem'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
          {editingObjective ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Objective</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  value={selectedObjective}
                  onChange={(e) => setSelectedObjective(e.target.value)}
                  disabled={savingAll || !isCampaignDraft}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    backgroundColor: '#fff',
                    cursor: savingAll ? 'not-allowed' : 'pointer'
                  }}
                >
                  {validObjectives.map(obj => (
                    <option key={obj} value={obj}>{objectiveLabel(obj)}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setEditingObjective(false);
                    const obj = getFieldValue(campaignFields, 'campaign.objective') || campaign?.objective;
                    setSelectedObjective(obj ? obj.toUpperCase() : '');
                  }}
                  disabled={savingAll}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#fff',
                    color: '#1F1F1F',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: savingAll ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <FieldRow
                  label="Objective"
                  value={objective ? objectiveLabel(objective.toUpperCase()) : ''}
                  summary={getField(campaignFields, 'campaign.objective')?.explanation_summary}
                  explanation={getField(campaignFields, 'campaign.objective')?.explanation}
                  confidence={getField(campaignFields, 'campaign.objective')?.confidence}
                />
              </div>
              {isCampaignDraft && (
                <button
                  onClick={() => setEditingObjective(true)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#1A1A19',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    alignSelf: 'flex-start',
                    marginTop: '0.5rem'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
          {editingBudget ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Budget</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Budget Type</label>
                  <select
                    value={budgetStrategy}
                    onChange={(e) => setBudgetStrategy(e.target.value)}
                    disabled={savingAll || !isCampaignDraft}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E5E5E5',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: '#fff',
                      cursor: savingAll ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="daily">Daily Budget</option>
                    <option value="lifetime">Lifetime Budget</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                    {budgetStrategy === 'daily' ? 'Daily Budget (in dollars)' : 'Lifetime Budget (in dollars)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={budgetAmount ? (budgetAmount / 100).toFixed(2) : ''}
                    onChange={(e) => {
                      const dollars = parseFloat(e.target.value) || 0;
                      setBudgetAmount(Math.round(dollars * 100)); // Convert to cents
                    }}
                    disabled={savingAll || !isCampaignDraft}
                    placeholder={budgetStrategy === 'daily' ? 'e.g., 50.00' : 'e.g., 1000.00'}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E5E5E5',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: '#fff',
                      cursor: savingAll ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      setEditingBudget(false);
                      const hasLifetime = budgetTotal && budgetTotal > 0;
                      const hasDaily = budgetDaily && budgetDaily > 0;
                      setBudgetStrategy(hasLifetime ? 'lifetime' : hasDaily ? 'daily' : 'daily');
                      setBudgetAmount(budgetTotal || budgetDaily || '');
                    }}
                    disabled={savingAll}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#fff',
                      color: '#1F1F1F',
                      border: '1px solid #E5E5E5',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: savingAll ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : shouldShowBudget ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <FieldRow
                  label="Budget"
                  value={
                    budgetType === 'cbo' 
                      ? 'CBO (Campaign Budget Optimization)' + (budgetTotal ? ` - ${formatCurrency(budgetTotal)} lifetime` : budgetDaily ? ` - ${formatCurrency(budgetDaily)}/day` : '')
                      : budgetTotal 
                        ? formatCurrency(budgetTotal) + ' lifetime' 
                        : budgetDaily 
                          ? formatCurrency(budgetDaily) + '/day' 
                          : null
                  }
                  summary={getField(campaignFields, 'campaign.budget_amount_total')?.explanation_summary ||
                          getField(campaignFields, 'campaign.budget_amount_daily')?.explanation_summary ||
                          getField(campaignFields, 'campaign.budget_type')?.explanation_summary}
                  explanation={getField(campaignFields, 'campaign.budget_amount_total')?.explanation ||
                              getField(campaignFields, 'campaign.budget_amount_daily')?.explanation ||
                              getField(campaignFields, 'campaign.budget_type')?.explanation ||
                              'Budget sets your total or daily spend. Meta distributes it across your ad sets to optimize for your objective.'}
                  confidence={getField(campaignFields, 'campaign.budget_amount_total')?.confidence ||
                             getField(campaignFields, 'campaign.budget_amount_daily')?.confidence ||
                             getField(campaignFields, 'campaign.budget_type')?.confidence}
                />
              </div>
              {isCampaignDraft && (
                <button
                  onClick={() => setEditingBudget(true)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#1A1A19',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    alignSelf: 'flex-start',
                    marginTop: '0.5rem'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <FieldRow
                  label="Budget"
                  value="Not set"
                  summary="Budget not configured"
                  explanation="No budget has been set for this campaign."
                  confidence="low"
                />
              </div>
              {isCampaignDraft && (
                <button
                  onClick={() => setEditingBudget(true)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#1A1A19',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    alignSelf: 'flex-start',
                    marginTop: '0.5rem'
                  }}
                >
                  Set Budget
                </button>
              )}
            </div>
          )}
          {editingSchedule ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1F1F1F' }}>Schedule</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Start Date & Time</label>
                  <input
                    type="datetime-local"
                    value={scheduleStart}
                    onChange={(e) => setScheduleStart(e.target.value)}
                    disabled={savingSchedule}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #E5E5E5', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={scheduleEnd}
                    onChange={(e) => setScheduleEnd(e.target.value)}
                    min={scheduleStart || undefined}
                    disabled={savingSchedule}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #E5E5E5', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                <button
                  className="cr-btn cr-btn--primary"
                  disabled={savingSchedule || !scheduleStart || !scheduleEnd}
                  onClick={async () => {
                    setSavingSchedule(true);
                    try {
                      const res = await fetch(`/api/facebook/campaigns/${campaign.id}`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Accept': 'application/json',
                          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                        },
                        body: JSON.stringify({ start_time: scheduleStart, end_time: scheduleEnd })
                      });
                      const data = await res.json();
                      if (res.ok && data.success) {
                        setEditingSchedule(false);
                        if (onRefetchCampaign) onRefetchCampaign();
                      }
                    } catch (e) {
                      console.error('Error saving schedule:', e);
                    } finally {
                      setSavingSchedule(false);
                    }
                  }}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  {savingSchedule ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingSchedule(false)}
                  disabled={savingSchedule}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', fontStyle: 'italic' }}>
                Ad set schedules in the Targeting tab can be customized within this window.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <FieldRow
                  label="Schedule"
                  value={startAt && endAt ? `${formatDate(startAt)} → ${formatDate(endAt)}` : null}
                  summary={analysisMeta.campaign_start_at?.reason || analysisMeta.campaign_end_at?.reason || getField(campaignFields, 'campaign.start_at')?.explanation_summary || getField(campaignFields, 'campaign.end_at')?.explanation_summary}
                  explanation={(() => {
                    const startReason = analysisMeta.campaign_start_at?.reason || getField(campaignFields, 'campaign.start_at')?.explanation;
                    const endReason = analysisMeta.campaign_end_at?.reason || getField(campaignFields, 'campaign.end_at')?.explanation;
                    if (startReason && endReason) return `Start: ${startReason}\n\nEnd: ${endReason}`;
                    if (startReason) return `Start: ${startReason}`;
                    if (endReason) return `End: ${endReason}`;
                    return 'This start/end window defines when your campaign runs. Meta optimizes delivery within this period for your chosen objective.';
                  })()}
                  confidence={analysisMeta.campaign_start_at?.confidence || getField(campaignFields, 'campaign.start_at')?.confidence}
                />
              </div>
              {isCampaignDraft && (
                <button
                  onClick={() => {
                    setScheduleStart(startAt ? utcToLocalDatetime(startAt) : '');
                    setScheduleEnd(endAt ? utcToLocalDatetime(endAt) : '');
                    setEditingSchedule(true);
                  }}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#1A1A19',
                    border: '1px solid #E5E5E5',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    alignSelf: 'flex-start',
                    marginTop: '0.5rem'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
        </div>

        {/* Internal Strategy Notes */}
        {internalNotes && (
          <div className="cr-panel__notes">
            <h4 className="cr-section-label">Strategy Summary</h4>
            <p className="cr-panel__notes-text">{safeRender(internalNotes)}</p>
          </div>
        )}
      </div>

      {/* Footer with single Save button - saves name, objective, budget in sequence, then syncs campaign (e.g. ad account) */}
      {onSave && isCampaignDraft && (
        <footer className="cr-drawer__footer" style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E5E5E5',
          padding: '1.5rem 0',
          marginTop: '2rem'
        }}>
          <div className="cr-drawer__footer-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
            <button
              className="cr-btn cr-btn--primary"
              onClick={handleSaveAll}
              disabled={savingAll}
              style={{ width: '100%' }}
            >
              {savingAll ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

const TargetingPanel = ({ fullResponse, campaign, toast, onRefetchCampaign, scopeUrl }) => {
  const rawAdSets = fullResponse?.ad_sets || [];
  const adSets = useMemo(() => [...rawAdSets].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)), [rawAdSets]);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [pixels, setPixels] = useState([]);
  const [pixelsLoading, setPixelsLoading] = useState(false);
  const [syncPixelsLoading, setSyncPixelsLoading] = useState(false);

  useEffect(() => {
    setPixelsLoading(true);
    fetch(scopeUrl('/api/facebook/pixels'), { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.pixels)) {
          setPixels(data.pixels);
        }
        setPixelsLoading(false);
      })
      .catch(() => setPixelsLoading(false));
  }, []);

  const handleSyncPixels = async () => {
    setSyncPixelsLoading(true);
    try {
      const res = await fetch(scopeUrl('/api/facebook/pixels/sync'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.pixels)) {
        setPixels(data.pixels);
        if (toast) toast.success(data.message || 'Pixels synced');
      } else if (data.success && data.job_queued) {
        if (toast) toast.success('Pixel sync started. Refresh in a moment.');
        const refetch = () => fetch(scopeUrl('/api/facebook/pixels'), { headers: { Accept: 'application/json' } })
          .then(r => r.json())
          .then(d => d.pixels && setPixels(d.pixels));
        setTimeout(refetch, 3000);
      } else {
        if (toast) toast.error(data.error || 'Sync failed');
      }
    } catch (e) {
      if (toast) toast.error(e.message || 'Sync failed');
    } finally {
      setSyncPixelsLoading(false);
    }
  };

  const campaignObjective = (campaign?.objective || getFieldValue(fullResponse?.campaign?.fields || [], 'campaign.objective') || '').toUpperCase();
  // Show pixel fields for OUTCOME_SALES (required) and OUTCOME_TRAFFIC (optional)
  const showPixelOption = campaignObjective === 'OUTCOME_SALES' || campaignObjective === 'OUTCOME_TRAFFIC';

  if (adSets.length === 0) {
    return (
      <div className="cr-panel cr-panel--targeting">
        <div className="cr-panel__empty">
          <p>No ad sets available. Generate a campaign to see targeting recommendations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-panel cr-panel--targeting">
      <div className="cr-panel__section">
        <h3 className="cr-panel__section-title">Ad Set Settings</h3>
      </div>

      <div className="cr-adsets">
        {adSets.map((adSet, index) => (
          <AdSetCard
            key={adSet.ad_set_key || index}
            adSet={adSet}
            index={index}
            isExpanded={expandedIndex === index}
            onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
            campaign={campaign}
            fullResponse={fullResponse}
            pixels={pixels}
            onRefetchCampaign={onRefetchCampaign}
            onSyncPixels={showPixelOption ? handleSyncPixels : null}
            syncPixelsLoading={syncPixelsLoading}
            pixelsLoading={pixelsLoading}
            scopeUrl={scopeUrl}
            onSaveAdSet={(type, message) => {
              // Handle save callback - show toast and refetch so other tabs show updated values
              if (toast) {
                if (type === 'success') {
                  toast.success(message);
                  onRefetchCampaign?.();
                } else {
                  toast.error(message);
                }
              } else {
                alert(message);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

const CreativePanel = ({ fullResponse, onSelectionChange, campaignId, onAdPublished, onSaveAd, scopeUrl }) => {
  const adSets = fullResponse?.ad_sets || [];
  const [publishingAds, setPublishingAds] = useState({}); // Track which ads are being published
  const [savingAds, setSavingAds] = useState({}); // Track which ads are being saved
  const [facebookPages, setFacebookPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  
  // Track selected page_id for each ad: { adIndex: page_id }
  const [selectedPageIds, setSelectedPageIds] = useState({});
  
  // Track link and CTA type for each ad: { adIndex: { link: '', cta_type: '' } }
  const [creativeData, setCreativeData] = useState({});
  
  // Track ad name for each ad: { adIndex: name }
  const [adNames, setAdNames] = useState({});
  
  // Track creative name for each ad: { adIndex: name }
  const [creativeNames, setCreativeNames] = useState({});

  // Flatten all ads from all ad sets in stable order: ad sets by id, then ads by id (no created_at sort)
  const allAds = useMemo(() => {
    const sortedAdSets = [...(adSets || [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    const ads = [];
    sortedAdSets.forEach((adSet, adSetIndex) => {
      const adSetName = getFieldValue(adSet.fields || [], 'ad_set.name', `Ad Set ${adSetIndex + 1}`);
      const adSetAds = (adSet.ads || []).slice();
      adSetAds.sort((x, y) => {
        const xId = getFieldValue(x.fields || [], 'ad.id');
        const yId = getFieldValue(y.fields || [], 'ad.id');
        const xi = xId != null && xId !== '' ? Number(xId) : Number.MAX_SAFE_INTEGER;
        const yi = yId != null && yId !== '' ? Number(yId) : Number.MAX_SAFE_INTEGER;
        return xi - yi;
      });
      adSetAds.forEach((ad) => {
        const fields = ad.fields || [];
        const adId = getFieldValue(fields, 'ad.id');
        const adStatus = getFieldValue(fields, 'ad.status', 'draft');
        const creative = ad.creative || {};
        ads.push({
          ...ad,
          adSetName: safeRender(adSetName),
          globalIndex: ads.length,
          adId: adId,
          adStatus: adStatus,
          creative: creative,
        });
      });
    });
    return ads;
  }, [adSets]);

  const [selectedAdIndex, setSelectedAdIndex] = useState(0);
  const selectedAd = allAds[selectedAdIndex];

  // Fetch Facebook pages on mount
  useEffect(() => {
    setLoadingPages(true);
    fetch(scopeUrl('/api/facebook/connection_status'), { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.pages)) {
          setFacebookPages(data.pages.filter(page => page.active));
        }
        setLoadingPages(false);
      })
      .catch(() => {
        setLoadingPages(false);
      });
  }, []);

  // Initialize page_id, link, and CTA type from database for each ad
  useEffect(() => {
    if (allAds.length === 0) return;
    
    const initialPageIds = {};
    const initialCreativeData = {};
    const initialAdNames = {};
    const initialCreativeNames = {};
    
    allAds.forEach((ad, index) => {
      // Try multiple sources for page_id
      const creative = ad.creative || {};
      const fields = ad.fields || [];
      
      // Check creative.page_id first (from API response with include_details)
      let dbPageId = creative.page_id;
      
      // If not found, check fields array (from serializer)
      if (!dbPageId) {
        dbPageId = getFieldValue(fields, 'ad.page_id');
      }
      
      // Also check if page_id is directly on the ad object (from different API responses)
      if (!dbPageId && ad.page_id) {
        dbPageId = ad.page_id;
      }
      
      // Convert to string for comparison with select value
      if (dbPageId) {
        initialPageIds[index] = String(dbPageId);
      }
      
      // Get link, CTA type, and media
      const dbLink = creative.link || getFieldValue(fields, 'ad.destination_url') || '';
      const dbCtaType = creative.cta_type || getFieldValue(fields, 'ad.call_to_action') || 'BUY_TICKETS';
      const dbMediaUrl = creative.media_url || getFieldValue(fields, 'ad.creative.media_url') || '';
      const dbMediaType = creative.media_type || getFieldValue(fields, 'ad.creative.media_type') || '';
      
      initialCreativeData[index] = {
        link: dbLink,
        cta_type: dbCtaType,
        media_url: dbMediaUrl,
        media_type: dbMediaType
      };
      
      // Get ad name (from ad.name field, not creative.name)
      const dbAdName = getFieldValue(fields, 'ad.name') || `Ad ${index + 1}`;
      initialAdNames[index] = dbAdName;
      
      // Get creative name (separate from ad name - from ad.creative.name field or creative.name)
      // Do NOT fallback to ad name - creative name should be independent
      const dbCreativeName = getFieldValue(fields, 'ad.creative.name') || creative.name || `Creative ${index + 1}`;
      initialCreativeNames[index] = dbCreativeName;
    });
    
    // Only update if we found page_ids or if the structure changed
    setSelectedPageIds(prev => {
      const hasChanges = Object.keys(initialPageIds).some(key => 
        prev[key] !== initialPageIds[key]
      ) || Object.keys(initialPageIds).length !== Object.keys(prev).length;
      
      return hasChanges ? { ...prev, ...initialPageIds } : prev;
    });
    
    // Update creative data
    setCreativeData(prev => {
      const hasChanges = Object.keys(initialCreativeData).some(key => {
        const existing = prev[key];
        const updated = initialCreativeData[key];
        return !existing ||
          existing.link !== updated.link ||
          existing.cta_type !== updated.cta_type ||
          existing.media_url !== updated.media_url ||
          existing.media_type !== updated.media_type;
      }) || Object.keys(initialCreativeData).length !== Object.keys(prev).length;
      
      return hasChanges ? { ...prev, ...initialCreativeData } : prev;
    });
    
    // Update creative names: only fill from server when user hasn't set a value for that ad index (preserve in-progress edits)
    setCreativeNames(prev => {
      const next = {};
      Object.keys(initialCreativeNames).forEach(key => {
        const i = parseInt(key, 10);
        next[i] = (prev[i] !== undefined && prev[i] !== null) ? prev[i] : initialCreativeNames[i];
      });
      return next;
    });
    
    // Update ad names: only fill from server when user hasn't set a value for that ad index (preserve in-progress edits)
    setAdNames(prev => {
      const next = {};
      Object.keys(initialAdNames).forEach(key => {
        const i = parseInt(key, 10);
        next[i] = (prev[i] !== undefined && prev[i] !== null) ? prev[i] : initialAdNames[i];
      });
      return next;
    });
  }, [allAds.length, allAds]);

  // Track selected indexes for each ad: { adIndex: { headline: 0, message: 0, description: 0 } }
  // Initialize from database if available
  const [selectedIndexes, setSelectedIndexes] = useState(() => {
    const initial = {};
    allAds.forEach((ad, index) => {
      const fields = ad.fields || [];
      // Get selected indexes from database (stored in fields)
      const dbMessageIndex = getFieldValue(fields, 'ad.selected_message_index', 0);
      const dbHeadlineIndex = getFieldValue(fields, 'ad.selected_headline_index', 0);
      const dbDescriptionIndex = getFieldValue(fields, 'ad.selected_description_index', 0);
      
      initial[index] = { 
        headline: dbHeadlineIndex, 
        message: dbMessageIndex, 
        description: dbDescriptionIndex 
      };
    });
    return initial;
  });

  // Update selected indexes when allAds changes (e.g., when campaign data is refreshed)
  useEffect(() => {
    const initial = {};
    allAds.forEach((ad, index) => {
      const fields = ad.fields || [];
      // Get selected indexes from database (stored in fields)
      const dbMessageIndex = getFieldValue(fields, 'ad.selected_message_index', 0);
      const dbHeadlineIndex = getFieldValue(fields, 'ad.selected_headline_index', 0);
      const dbDescriptionIndex = getFieldValue(fields, 'ad.selected_description_index', 0);
      
      // Use existing state if available, otherwise use database values
      if (selectedIndexes[index]) {
        initial[index] = selectedIndexes[index];
      } else {
        initial[index] = { 
          headline: dbHeadlineIndex, 
          message: dbMessageIndex, 
          description: dbDescriptionIndex 
        };
      }
    });
    // Update if structure changed or if we need to sync with database
    const needsUpdate = Object.keys(initial).length !== Object.keys(selectedIndexes).length ||
      Object.keys(initial).some(key => {
        const existing = selectedIndexes[key];
        const updated = initial[key];
        return !existing || 
          existing.headline !== updated.headline || 
          existing.message !== updated.message || 
          existing.description !== updated.description;
      });
    
    if (needsUpdate) {
      setSelectedIndexes(initial);
    }
  }, [allAds.length, allAds]); // Re-initialize when ads change

  // Update selected indexes when ad changes
  useEffect(() => {
    if (onSelectionChange && Object.keys(selectedIndexes).length > 0) {
      onSelectionChange(selectedIndexes);
    }
  }, [selectedIndexes, onSelectionChange]);

  const handleHeadlineIndexChange = (index) => {
    setSelectedIndexes(prev => ({
      ...prev,
      [selectedAdIndex]: {
        ...prev[selectedAdIndex],
        headline: index
      }
    }));
  };

  const handleMessageIndexChange = (index) => {
    setSelectedIndexes(prev => ({
      ...prev,
      [selectedAdIndex]: {
        ...prev[selectedAdIndex],
        message: index
      }
    }));
  };

  const handleDescriptionIndexChange = (index) => {
    setSelectedIndexes(prev => ({
      ...prev,
      [selectedAdIndex]: {
        ...prev[selectedAdIndex],
        description: index
      }
    }));
  };

  if (allAds.length === 0) {
    return (
      <div className="cr-panel cr-panel--creative">
        <div className="cr-panel__empty">
          <p>No ads available. Generate a campaign to see creative recommendations.</p>
        </div>
      </div>
    );
  }

  const fields = selectedAd?.fields || [];
  const headlines = getFieldValue(fields, 'ad.copy.headline_variations', []);
  const primaryTexts = getFieldValue(fields, 'ad.copy.primary_text_variations', []);
  const descriptions = getFieldValue(fields, 'ad.copy.description_variations', []);
  const notes = getFieldValue(fields, 'ad.notes');

  const currentIndexes = selectedIndexes[selectedAdIndex] || { headline: 0, message: 0, description: 0 };
  const currentPageId = selectedPageIds[selectedAdIndex] || '';
  const currentCreative = creativeData[selectedAdIndex] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' };
  const currentLink = currentCreative.link || '';
  const currentCtaType = currentCreative.cta_type || 'BUY_TICKETS';
  const currentMediaUrl = currentCreative.media_url || '';
  const currentMediaType = currentCreative.media_type || '';
  const isVideoMedia = currentMediaType && String(currentMediaType).toLowerCase().includes('video') ||
    (currentMediaUrl && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(currentMediaUrl));

  const facebookReviewStatus = getFieldValue(selectedAd?.fields || [], 'ad.facebook_review_status');
  const facebookDeliveryStatus = getFieldValue(selectedAd?.fields || [], 'ad.facebook_delivery_status');
  const facebookRejectionReason = getFieldValue(selectedAd?.fields || [], 'ad.facebook_rejection_reason');

  // Edit/override only when ad is in draft (or failed); disable when paused/scheduled/published
  const isAdDraft = selectedAd?.adStatus === 'draft' || selectedAd?.adStatus === 'DRAFT' || selectedAd?.adStatus === 'failed';
  const isAdPausedOrScheduled = !isAdDraft;

  const uploadCareContainerRef = useRef(null);
  const uploadCareInputRef = useRef(null);
  const uploadCareWidgetRef = useRef(null);
  const selectedAdIndexRef = useRef(selectedAdIndex);
  selectedAdIndexRef.current = selectedAdIndex;

  // Shared: build payload and PATCH ad (used by both Save Changes and Approve & Schedule)
  const saveAdChanges = useCallback(async () => {
    if (!selectedAd?.adId) return null;
    const adSelectedIndexes = currentIndexes;
    const currentAdName = adNames[selectedAdIndex] || getFieldValue(selectedAd.fields || [], 'ad.name', `Ad ${selectedAdIndex + 1}`);
    const currentCreativeName = creativeNames[selectedAdIndex] || getFieldValue(selectedAd.fields || [], 'ad.creative.name') || getFieldValue(selectedAd.creative || {}, 'name') || currentAdName;
    const creativeUpdateData = {
      name: currentCreativeName,
      selected_message_index: adSelectedIndexes.message || 0,
      selected_headline_index: adSelectedIndexes.headline || 0,
      selected_description_index: adSelectedIndexes.description || 0,
    };
    if (currentPageId) creativeUpdateData.page_id = currentPageId;
    if (currentLink) creativeUpdateData.link = currentLink;
    if (currentCtaType) creativeUpdateData.cta_type = currentCtaType;
    if (currentMediaUrl) {
      creativeUpdateData.media_url = currentMediaUrl;
      creativeUpdateData.media_type = currentMediaType || 'image/jpeg';
    } else {
      creativeUpdateData.media_url = '';
      creativeUpdateData.media_type = '';
    }
    const response = await fetch(`/facebook_ads/${selectedAd.adId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({ name: currentAdName, creative: creativeUpdateData })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }, [selectedAd, selectedAdIndex, currentIndexes, adNames, creativeNames, currentPageId, currentLink, currentCtaType, currentMediaUrl, currentMediaType]);

  const handlePageChange = (e) => {
    const newPageId = e.target.value;
    setSelectedPageIds(prev => ({
      ...prev,
      [selectedAdIndex]: newPageId || null
    }));
  };

  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setCreativeData(prev => ({
      ...prev,
      [selectedAdIndex]: {
        ...prev[selectedAdIndex] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' },
        link: newLink
      }
    }));
  };

  const handleCtaTypeChange = (e) => {
    const newCtaType = e.target.value;
    setCreativeData(prev => ({
      ...prev,
      [selectedAdIndex]: {
        ...prev[selectedAdIndex] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' },
        cta_type: newCtaType
      }
    }));
  };

  // Update page_id, link, and CTA when switching ads (sync with database if available)
  useEffect(() => {
    if (!selectedAd) return;
    
    const creative = selectedAd.creative || {};
    const fields = selectedAd.fields || [];
    
    // Try to get page_id from multiple sources
    let dbPageId = creative.page_id;
    if (!dbPageId) {
      dbPageId = getFieldValue(fields, 'ad.page_id');
    }
    if (!dbPageId && selectedAd.page_id) {
      dbPageId = selectedAd.page_id;
    }
    
    // Only update if we have a page_id from database and it's different from current state
    if (dbPageId) {
      const pageIdString = String(dbPageId);
      setSelectedPageIds(prev => {
        if (prev[selectedAdIndex] !== pageIdString) {
          return {
            ...prev,
            [selectedAdIndex]: pageIdString
          };
        }
        return prev;
      });
    }
    
    // Update link, CTA type, and media
    const dbLink = creative.link || getFieldValue(fields, 'ad.destination_url') || '';
    const dbCtaType = creative.cta_type || getFieldValue(fields, 'ad.call_to_action') || 'BUY_TICKETS';
    const dbMediaUrl = creative.media_url || getFieldValue(fields, 'ad.creative.media_url') || '';
    const dbMediaType = creative.media_type || getFieldValue(fields, 'ad.creative.media_type') || '';
    
    setCreativeData(prev => {
      const current = prev[selectedAdIndex] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' };
      if (current.link !== dbLink || current.cta_type !== dbCtaType || current.media_url !== dbMediaUrl || current.media_type !== dbMediaType) {
        return {
          ...prev,
          [selectedAdIndex]: {
            link: dbLink,
            cta_type: dbCtaType,
            media_url: dbMediaUrl,
            media_type: dbMediaType
          }
        };
      }
      return prev;
    });
  }, [selectedAdIndex, selectedAd]);

  // Initialize Uploadcare widget (same pattern as Org Settings Profile)
  useEffect(() => {
    if (!isAdDraft || currentMediaUrl || !uploadCareContainerRef.current) return;
    const containerEl = uploadCareContainerRef.current;
    const initWidget = () => {
      if (typeof window.uploadcare === 'undefined') return;
      try {
        uploadCareWidgetRef.current = null;
        const widgets = window.uploadcare.initialize ? window.uploadcare.initialize(containerEl) : [];
        const widget = widgets && widgets.length > 0 ? widgets[0] : window.uploadcare.SingleWidget(uploadCareInputRef.current);
        if (widget) {
          uploadCareWidgetRef.current = widget;
          widget.onChange(function (file) {
            if (file) {
              file.done(function (fileInfo) {
                const idx = selectedAdIndexRef.current;
                const url = fileInfo.cdnUrl || fileInfo.url || (fileInfo.uuid && `https://ucarecdn.com/${fileInfo.uuid}/`);
                const mime = fileInfo.mimeType || fileInfo.mime_type || '';
                const mediaType = (mime && mime.toLowerCase().startsWith('video')) ? 'video' : (mime || 'image/jpeg');
                if (url) {
                  setCreativeData(prev => ({
                    ...prev,
                    [idx]: {
                      ...prev[idx] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' },
                      media_url: url,
                      media_type: mediaType
                    }
                  }));
                }
              });
            }
          });
        }
      } catch (e) {
        console.warn('Uploadcare widget init:', e);
      }
    };
    if (window.uploadcare) {
      initWidget();
    } else {
      const onReady = () => initWidget();
      window.addEventListener('uploadcare:ready', onReady);
      return () => window.removeEventListener('uploadcare:ready', onReady);
    }
    return () => { uploadCareWidgetRef.current = null; };
  }, [isAdDraft, currentMediaUrl, selectedAdIndex]);

  return (
    <div className="cr-panel cr-panel--creative">
      {!isAdDraft && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FFF4E6',
          border: '1px solid #FFD89B',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#8B4513',
          fontSize: '0.875rem'
        }}>
          This ad has been published or scheduled. Edits are only available when the ad is in draft or failed.
        </div>
      )}
      {String(selectedAd?.status || '').toLowerCase() === 'failed' && selectedAd?.error_message && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FFEBEE',
          border: '1px solid #ef5350',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#c62828',
          fontSize: '0.875rem'
        }}>
          <strong>Creative Error:</strong> {selectedAd.error_message}
        </div>
      )}
      {String(selectedAd?.adStatus || '').toLowerCase() === 'failed' && getFieldValue(selectedAd?.fields || [], 'ad.error_message') && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FFEBEE',
          border: '1px solid #ef5350',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#c62828',
          fontSize: '0.875rem'
        }}>
          <strong>Ad Error:</strong> {getFieldValue(selectedAd.fields || [], 'ad.error_message')}
        </div>
      )}
      {/* Facebook status (synced by CheckFacebookAdStatusJob) */}
      {(facebookReviewStatus || facebookDeliveryStatus) && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#F5F5F5',
          border: '1px solid #E5E5E5',
          borderRadius: '6px',
          fontSize: '0.875rem'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#1F1F1F' }}>Facebook status</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
            {facebookReviewStatus && (
              <span>
                <span style={{ color: '#666' }}>Review: </span>
                <span style={{
                  color: facebookReviewStatus === 'APPROVED' ? '#0a7c42' : facebookReviewStatus === 'REJECTED' ? '#c00' : '#b8860b',
                  fontWeight: 500
                }}>
                  {facebookReviewStatus === 'APPROVED' && 'Approved'}
                  {facebookReviewStatus === 'REJECTED' && 'Rejected'}
                  {facebookReviewStatus === 'IN_REVIEW' && 'In review'}
                  {!['APPROVED', 'REJECTED', 'IN_REVIEW'].includes(facebookReviewStatus) && facebookReviewStatus}
                </span>
              </span>
            )}
            {facebookDeliveryStatus && (
              <span>
                <span style={{ color: '#666' }}>Delivery: </span>
                <span style={{ fontWeight: 500, color: '#1F1F1F' }}>{facebookDeliveryStatus}</span>
              </span>
            )}
          </div>
          {facebookRejectionReason && (
            <div style={{ marginTop: '0.5rem', color: '#c00', fontSize: '0.8125rem' }}>
              Rejection reason: {facebookRejectionReason}
            </div>
          )}
        </div>
      )}
      {/* Page selector */}
      <div className="cr-page-selector" style={{ 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#F9F9F9', 
        borderRadius: '8px',
        border: '1px solid #E5E5E5'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: 500, 
          fontSize: '0.875rem',
          color: '#1F1F1F'
        }}>
          Facebook Page
        </label>
        {loadingPages ? (
          <span style={{ color: '#666', fontSize: '0.875rem' }}>Loading pages...</span>
        ) : facebookPages.length > 0 ? (
          <select
            value={currentPageId}
            onChange={handlePageChange}
            disabled={isAdPausedOrScheduled}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: isAdPausedOrScheduled ? '#F5F5F5' : '#fff',
              cursor: isAdPausedOrScheduled ? 'not-allowed' : 'pointer',
              color: isAdPausedOrScheduled ? '#999' : '#1F1F1F'
            }}
          >
            <option value="">Select a Facebook Page</option>
            {facebookPages.map(page => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>
        ) : (
          <span style={{ color: '#999', fontSize: '0.875rem' }}>No Facebook pages available. Please connect a Facebook page in settings.</span>
        )}
        {isAdPausedOrScheduled && (
          <p style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.75rem', 
            color: '#8B4513',
            marginBottom: 0
          }}>
            Page selection is disabled for {selectedAd?.adStatus === 'paused' ? 'paused' : selectedAd?.adStatus === 'scheduled' ? 'scheduled' : 'published'} ads.
          </p>
        )}
      </div>

      {/* Ad selector */}
      <div className="cr-creative-selector">
        <span className="cr-creative-selector__label">Viewing ad:</span>
        <div className="cr-creative-selector__pills">
          {allAds.map((ad, index) => {
            return (
              <button
                key={index}
                className={`cr-creative-selector__pill ${selectedAdIndex === index ? 'cr-creative-selector__pill--active' : ''}`}
                onClick={() => setSelectedAdIndex(index)}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
          <div>
            <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.25rem' }}>
              <label style={{ fontSize: '0.75rem', color: '#666', display: 'block', margin: 0 }}>Ad Name</label>
              {(getField(fields, 'ad.name')?.explanation) && (
                <InfoWithTooltip text={getField(fields, 'ad.name')?.explanation} />
              )}
            </span>
            <input
              type="text"
              value={adNames[selectedAdIndex] !== undefined && adNames[selectedAdIndex] !== null ? adNames[selectedAdIndex] : (getFieldValue(selectedAd.fields || [], 'ad.name') ?? `Ad ${selectedAdIndex + 1}`)}
              onChange={(e) => setAdNames(prev => {
                const updated = { ...(prev || {}) };
                updated[selectedAdIndex] = e.target.value;
                return updated;
              })}
              disabled={!isAdDraft}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #E5E5E5',
                borderRadius: '4px',
                fontSize: '0.875rem',
                backgroundColor: isAdDraft ? '#fff' : '#F5F5F5',
                cursor: isAdDraft ? 'text' : 'not-allowed',
                opacity: isAdDraft ? 1 : 0.8
              }}
              placeholder="Ad name"
            />
          </div>
          <div>
            <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.25rem' }}>
              <label style={{ fontSize: '0.75rem', color: '#666', display: 'block', margin: 0 }}>Creative Name</label>
              {(getField(fields, 'ad.creative.name')?.explanation) && (
                <InfoWithTooltip text={getField(fields, 'ad.creative.name')?.explanation} />
              )}
            </span>
            <input
              type="text"
              value={creativeNames[selectedAdIndex] !== undefined && creativeNames[selectedAdIndex] !== null ? creativeNames[selectedAdIndex] : (getFieldValue(selectedAd.fields || [], 'ad.creative.name') || getFieldValue(selectedAd.creative || {}, 'name') || `Creative ${selectedAdIndex + 1}`)}
              onChange={(e) => setCreativeNames(prev => {
                const updated = { ...(prev || {}) };
                updated[selectedAdIndex] = e.target.value;
                return updated;
              })}
              disabled={!isAdDraft}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #E5E5E5',
                borderRadius: '4px',
                fontSize: '0.875rem',
                backgroundColor: isAdDraft ? '#fff' : '#F5F5F5',
                cursor: isAdDraft ? 'text' : 'not-allowed',
                opacity: isAdDraft ? 1 : 0.8
              }}
              placeholder="Creative name"
            />
          </div>
        </div>
      </div>

      {/* Ad set context */}
      <div className="cr-creative-context">
        <span className="cr-creative-context__label">From:</span>
        <span className="cr-creative-context__adset">{selectedAd.adSetName}</span>
      </div>

      {/* Ad angle/notes */}
      {notes && (
        <div className="cr-creative-angle">
          <span className="cr-creative-angle__label">Angle</span>
          <p className="cr-creative-angle__text">{safeRender(notes)}</p>
        </div>
      )}

      {/* Copy variations */}
      <div className="cr-creative-copy">
        {isAdPausedOrScheduled && (
          <div style={{ 
            padding: '0.75rem', 
            backgroundColor: '#FFF4E6', 
            border: '1px solid #FFD89B',
            borderRadius: '6px',
            marginBottom: '1rem',
            color: '#8B4513',
            fontSize: '0.875rem'
          }}>
            ⚠️ This ad is {selectedAd?.adStatus === 'paused' ? 'paused' : selectedAd?.adStatus === 'scheduled' ? 'scheduled' : 'published'}. Variation selection is disabled.
          </div>
        )}
        <CopyVariations
          label="Headlines"
          variations={Array.isArray(headlines) ? headlines : []}
          explanation={getField(fields, 'ad.copy.headline_variations')?.explanation || getField(fields, 'ad.copy.headline_variations')?.explanation_summary}
          selectedIndex={currentIndexes.headline}
          onSelectionChange={handleHeadlineIndexChange}
          disabled={isAdPausedOrScheduled}
        />
        <CopyVariations
          label="Primary Text"
          variations={Array.isArray(primaryTexts) ? primaryTexts : []}
          explanation={getField(fields, 'ad.copy.primary_text_variations')?.explanation || getField(fields, 'ad.copy.primary_text_variations')?.explanation_summary}
          selectedIndex={currentIndexes.message}
          onSelectionChange={handleMessageIndexChange}
          disabled={isAdPausedOrScheduled}
        />
        {Array.isArray(descriptions) && descriptions.length > 0 && (
          <CopyVariations
            label="Description"
            variations={descriptions}
            explanation={getField(fields, 'ad.copy.description_variations')?.explanation || getField(fields, 'ad.copy.description_variations')?.explanation_summary}
            selectedIndex={currentIndexes.description}
            onSelectionChange={handleDescriptionIndexChange}
            disabled={isAdPausedOrScheduled}
          />
        )}
      </div>

      {/* Ad image / video – Creative media */}
      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E5E5' }}>
        <label style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: '#1F1F1F'
        }}>
          {isVideoMedia ? 'Ad video' : 'Ad image'}
        </label>
        {currentMediaUrl ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {isVideoMedia ? (
                <video
                  src={currentMediaUrl}
                  controls
                  playsInline
                  style={{
                    maxWidth: '280px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    backgroundColor: '#F5F5F5'
                  }}
                />
              ) : (
                <img
                  src={currentMediaUrl}
                  alt="Ad creative"
                  style={{
                    maxWidth: '280px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    backgroundColor: '#F5F5F5'
                  }}
                />
              )}
              {isAdDraft && (
                <button
                  onClick={() => {
                    // Inline confirmation — toggle a data attribute to confirm
                    const btn = document.getElementById(`remove-media-${selectedAdIndex}`);
                    if (btn && btn.dataset.confirming === 'true') {
                      // Confirmed: clear media
                      const idx = selectedAdIndexRef.current;
                      setCreativeData(prev => ({
                        ...prev,
                        [idx]: {
                          ...prev[idx] || { link: '', cta_type: 'BUY_TICKETS', media_url: '', media_type: '' },
                          media_url: '',
                          media_type: ''
                        }
                      }));
                      btn.dataset.confirming = 'false';
                      btn.textContent = '×';
                    } else if (btn) {
                      btn.dataset.confirming = 'true';
                      btn.textContent = 'Remove?';
                      setTimeout(() => {
                        if (btn) {
                          btn.dataset.confirming = 'false';
                          btn.textContent = '×';
                        }
                      }, 3000);
                    }
                  }}
                  id={`remove-media-${selectedAdIndex}`}
                  data-confirming="false"
                  title="Remove image"
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    width: 'auto',
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 6px',
                    lineHeight: 1,
                    zIndex: 2
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ) : (
          isAdDraft && (
            <div ref={uploadCareContainerRef} className="uc-image-preview-single" style={{ marginTop: '0.5rem' }}>
              <input
                ref={uploadCareInputRef}
                type="hidden"
                role="uploadcare-uploader"
                data-crop="1200x1200"
                name="creative_ad_image"
                id="creative-ad-image-widget"
              />
            </div>
          )
        )}
        {!currentMediaUrl && !isAdDraft && (
          <span style={{ color: '#888', fontSize: '0.875rem' }}>No image or video. Edits available in draft.</span>
        )}
      </div>

      {/* Link and CTA */}
      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E5E5' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.5rem' }}>
            <label style={{ 
              display: 'block', 
              margin: 0, 
              fontWeight: 500, 
              fontSize: '0.875rem',
              color: '#1F1F1F'
            }}>
              Creative link
            </label>
            {(getField(fields, 'ad.destination_url')?.explanation) && (
              <InfoWithTooltip text={getField(fields, 'ad.destination_url')?.explanation} />
            )}
          </span>
          <input
            type="url"
            value={currentLink}
            onChange={handleLinkChange}
            disabled={isAdPausedOrScheduled}
            placeholder="https://example.com/event"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: isAdPausedOrScheduled ? '#F5F5F5' : '#fff',
              cursor: isAdPausedOrScheduled ? 'not-allowed' : 'text',
              color: isAdPausedOrScheduled ? '#999' : '#1F1F1F'
            }}
          />
        </div>
        
        <div>
          <span className="cr-field__label-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.5rem' }}>
            <label style={{ 
              display: 'block', 
              margin: 0, 
              fontWeight: 500, 
              fontSize: '0.875rem',
              color: '#1F1F1F'
            }}>
              Call to Action
            </label>
            {(getField(fields, 'ad.call_to_action')?.explanation) && (
              <InfoWithTooltip text={getField(fields, 'ad.call_to_action')?.explanation} />
            )}
          </span>
          <select
            value={currentCtaType}
            onChange={handleCtaTypeChange}
            disabled={isAdPausedOrScheduled}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: isAdPausedOrScheduled ? '#F5F5F5' : '#fff',
              cursor: isAdPausedOrScheduled ? 'not-allowed' : 'pointer',
              color: isAdPausedOrScheduled ? '#999' : '#1F1F1F'
            }}
          >
            {VALID_CTA_TYPES.map(ctaType => (
              <option key={ctaType} value={ctaType}>
                {CTA_LABELS[ctaType] || ctaType}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Individual Save Changes and Approve & Schedule buttons for this ad */}
      {selectedAd.adId && (
        <div className="cr-creative-actions" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E5E5' }}>
          {selectedAd.adStatus === 'draft' || selectedAd.adStatus === 'failed' ? (
            <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
              <button
                className="cr-btn cr-btn--primary"
                onClick={async () => {
                  if (!selectedAd.adId) return;
                  setSavingAds(prev => ({ ...prev, [selectedAdIndex]: true }));
                  try {
                    const data = await saveAdChanges();
                    if (data?.success) {
                      if (onSaveAd) onSaveAd(selectedAd.adId, 'success', 'Changes saved successfully!');
                      else alert('Changes saved successfully!');
                      if (onAdPublished) onAdPublished(selectedAd.adId);
                    } else {
                      const errorMsg = data?.error || 'Failed to save changes';
                      if (onSaveAd) onSaveAd(selectedAd.adId, 'error', errorMsg);
                      else alert(errorMsg);
                    }
                  } catch (error) {
                    console.error('Error saving ad changes:', error);
                    if (onSaveAd) onSaveAd(selectedAd.adId, 'error', 'Failed to save changes. Please try again.');
                    else alert('Failed to save changes. Please try again.');
                  } finally {
                    setSavingAds(prev => ({ ...prev, [selectedAdIndex]: false }));
                  }
                }}
                disabled={savingAds[selectedAdIndex] || publishingAds[selectedAdIndex]}
                style={{ width: '100%' }}
              >
                {savingAds[selectedAdIndex] ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="cr-btn cr-btn--primary"
                onClick={async () => {
                  if (!selectedAd.adId) return;
                  setPublishingAds(prev => ({ ...prev, [selectedAdIndex]: true }));
                  try {
                    const updateData = await saveAdChanges();
                    if (updateData && !updateData.success) {
                      const errorMsg = updateData.error || 'Failed to save changes';
                      if (onSaveAd) onSaveAd(selectedAd.adId, 'error', errorMsg);
                      else alert(errorMsg);
                      return;
                    }
                    const response = await fetch(`/facebook_ads/${selectedAd.adId}/schedule_and_approve`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                      },
                      body: JSON.stringify({ id: selectedAd.adId, status: 'scheduled' })
                    });
                    const data = await response.json();
                    if (data.success) {
                      if (onSaveAd) onSaveAd(selectedAd.adId, 'success', 'Ad approved and scheduled successfully!');
                      else alert('Ad approved and scheduled successfully!');
                      if (onAdPublished) onAdPublished(selectedAd.adId);
                    } else {
                      const errorMsg = data.error || 'Failed to publish ad';
                      if (onSaveAd) onSaveAd(selectedAd.adId, 'error', errorMsg);
                      else alert(errorMsg);
                    }
                  } catch (error) {
                    console.error('Error publishing ad:', error);
                    if (onSaveAd) onSaveAd(selectedAd.adId, 'error', 'Failed to publish ad. Please try again.');
                    else alert('Failed to publish ad. Please try again.');
                  } finally {
                    setPublishingAds(prev => ({ ...prev, [selectedAdIndex]: false }));
                  }
                }}
                disabled={publishingAds[selectedAdIndex] || savingAds[selectedAdIndex]}
                style={{ width: '100%' }}
              >
                {publishingAds[selectedAdIndex] ? 'Publishing...' : 'Approve & Schedule'}
              </button>
            </div>
          ) : (
            <div style={{ 
              padding: '0.75rem', 
              backgroundColor: '#F5F5F5', 
              borderRadius: '6px',
              textAlign: 'center',
              color: '#525250',
              fontSize: '0.875rem'
            }}>
              {selectedAd.adStatus === 'scheduled' ? 'Ad is scheduled' : 
               selectedAd.adStatus === 'published' ? 'Ad is published' : 
               `Ad status: ${selectedAd.adStatus}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'targeting', label: 'Targeting' },
  { id: 'creative', label: 'Creative' },
];

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DRAWER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const CampaignReview = ({ campaign, isOpen, onClose, onApprove, onReject, onArchive, onSave, renderAsPage = false, activeTab: externalActiveTab, onTabChange, toast, onRefetchCampaign, companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [isVisible, setIsVisible] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState('overview');
  
  // Use external tab state if provided (for page mode), otherwise use internal state (for modal mode)
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const setActiveTab = onTabChange || setInternalActiveTab;

  // Track selected indexes for variations
  const [selectedIndexes, setSelectedIndexes] = useState({});

  // Extract full response from campaign
  const fullResponse = useMemo(() => {
    return campaign?.fullResponse || campaign?.ai_explanations?._full_response?.value || null;
  }, [campaign]);

  const sources = fullResponse?.sources;

  // Handle selection change from CreativePanel
  const handleSelectionChange = (indexes) => {
    setSelectedIndexes(indexes);
  };

  // Enhanced save handler that includes selected indexes
  const handleSave = (action) => {
    const campaignWithIndexes = {
      ...campaign,
      selectedIndexes: selectedIndexes
    };
    if (onSave) {
      onSave(campaignWithIndexes, action);
    }
  };

  // Check if campaign is paused or has scheduled ads
  const isCampaignPausedOrScheduled = useMemo(() => {
    if (!campaign) return false;
    
    // Check campaign status
    if (campaign.status === 'PAUSED' || campaign.status === 'paused') {
      return true;
    }
    
    // Check if any ads are scheduled by looking at ad_sets -> ads in fullResponse
    // Ads are nested under ad_sets -> ads (or creatives)
    const adSets = fullResponse?.ad_sets || [];
    const hasScheduledAds = adSets.some(adSet => {
      const ads = adSet?.ads || adSet?.creatives || [];
      return ads.some(ad => {
        // Check status field in ad fields array
        const statusField = ad?.fields?.find(f => f.api_key === 'ad.status');
        const adStatus = statusField?.value || ad?.status;
        return adStatus === 'scheduled' || adStatus === 'published';
      });
    });
    
    return hasScheduledAds;
  }, [campaign, fullResponse]);

  // Synced = has Meta id or status is not draft (approved/synced campaigns)
  const isCampaignSynced = useMemo(() => {
    if (!campaign) return false;
    if (campaign.facebook_campaign_id) return true;
    const s = String(campaign.status || '').toLowerCase();
    return s && s !== 'draft';
  }, [campaign]);

  useEffect(() => {
    if (isOpen) {
      if (renderAsPage) {
        setIsVisible(true);
      } else {
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => setIsVisible(true));
        setActiveTab('overview');
      }
    } else {
      setIsVisible(false);
    }
    return () => {
      if (!renderAsPage) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, renderAsPage]);

  const handleClose = () => {
    if (renderAsPage) {
      onClose?.();
    } else {
      setIsVisible(false);
      setTimeout(onClose, 280);
    }
  };

  if (!isOpen && !isVisible && !renderAsPage) return null;
  
  // If rendering as page, return content without overlay
  if (renderAsPage) {
    return (
      <>
        {/* Tab Content */}
        <div className="cr-drawer__content" style={{ padding: 0 }}>
          {activeTab === 'overview' && (
            <>
              <OverviewPanel
                campaign={campaign}
                fullResponse={fullResponse}
                onAdAccountChange={(adAccountId) => {
                  // Store the ad account change to be saved with the campaign
                  if (campaign) {
                    campaign.facebook_ad_account_id = adAccountId;
                  }
                }}
                onSave={(campaignData, action) => {
                  // Use the existing onSave callback from CampaignReview
                  if (onSave) {
                    onSave(campaignData || campaign, action || "update");
                  }
                }}
                onRefetchCampaign={onRefetchCampaign}
                scopeUrl={scopeUrl}
              />
              <SourcesSection sources={sources} />
            </>
          )}
          {activeTab === 'targeting' && (
            <TargetingPanel fullResponse={fullResponse} campaign={campaign} toast={toast} onRefetchCampaign={onRefetchCampaign} scopeUrl={scopeUrl} />
          )}
          {activeTab === 'creative' && (
            <CreativePanel
              fullResponse={fullResponse}
              onSelectionChange={handleSelectionChange}
              campaignId={campaign?.id}
              onAdPublished={(adId) => {
                // Handle ad published callback if needed
                console.log('Ad published:', adId);
              }}
              onSaveAd={(adId, type, message) => {
                // Handle save callback - show toast and refetch so other tabs show updated values
                if (toast) {
                  if (type === 'success') {
                    toast.success(message);
                    onRefetchCampaign?.();
                  } else {
                    toast.error(message);
                  }
                } else {
                  alert(message);
                }
              }}
              scopeUrl={scopeUrl}
            />
          )}
        </div>

        {/* Creative tab: no campaign-level footer — each ad has its own Approve & Schedule button */}
      </>
    );
  }

  return (
    <div
      className={`cr-overlay ${isVisible ? 'cr-overlay--visible' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`cr-drawer ${isVisible ? 'cr-drawer--open' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <header className="cr-drawer__header">
          <div className="cr-drawer__header-top">
            {!isCampaignSynced && <span className="cr-drawer__badge">AI Draft Review</span>}
            <button className="cr-drawer__close" onClick={handleClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <h1 className="cr-drawer__title">{campaign?.campaign_name}</h1>
          <p className="cr-drawer__subtitle">
            {campaign?.display_event_name || 'Company-level campaign'}
          </p>
        </header>

        {/* Tab Navigation */}
        <nav className="cr-tabs">
          <div className="cr-tabs__track">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`cr-tabs__tab ${activeTab === tab.id ? 'cr-tabs__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
              >
                {tab.label}
              </button>
            ))}
            <div
              className="cr-tabs__indicator"
              style={{
                transform: `translateX(${TABS.findIndex(t => t.id === activeTab) * 100}%)`,
                width: `${100 / TABS.length}%`
              }}
            />
          </div>
        </nav>

        {/* Tab Content */}
        <div className="cr-drawer__content">
          {isCampaignSynced && (
            <p className="cr-drawer__synced-notice" style={{ margin: '0 0 1rem', padding: '0.75rem 1rem', background: '#f0f4f8', borderRadius: 8, fontSize: '0.875rem', color: '#374151' }}>
              This campaign has been synced to Meta Ads Manager and is currently paused. Edit in Meta going forward.
              {campaign?.meta_ads_manager_url && (
                <> {' '}
                  <a href={campaign.meta_ads_manager_url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                    Open in Meta Ads Manager →
                  </a>
                </>
              )}
            </p>
          )}
          {activeTab === 'overview' && (
            <OverviewPanel
              campaign={campaign}
              fullResponse={fullResponse}
              onAdAccountChange={(adAccountId) => {
                // Store the ad account change to be saved with the campaign
                if (campaign) {
                  campaign.facebook_ad_account_id = adAccountId;
                }
              }}
              onSave={(campaignData, action) => {
                // Use the existing onSave callback from CampaignReview
                if (onSave) {
                  onSave(campaignData || campaign, action || "update");
                }
              }}
              onRefetchCampaign={onRefetchCampaign}
              scopeUrl={scopeUrl}
            />
          )}
          {activeTab === 'targeting' && (
            <TargetingPanel fullResponse={fullResponse} campaign={campaign} toast={toast} onRefetchCampaign={onRefetchCampaign} scopeUrl={scopeUrl} />
          )}
          {activeTab === 'creative' && (
            <CreativePanel
              fullResponse={fullResponse}
              onSelectionChange={handleSelectionChange}
              campaignId={campaign?.id}
              onAdPublished={(adId) => {
                console.log('Ad published:', adId);
              }}
              onSaveAd={(adId, type, message) => {
                if (toast) {
                  if (type === 'success') {
                    toast.success(message);
                    onRefetchCampaign?.();
                  } else {
                    toast.error(message);
                  }
                } else {
                  alert(message);
                }
              }}
              scopeUrl={scopeUrl}
            />
          )}

          {/* Sources */}
          {activeTab === 'overview' && <SourcesSection sources={sources} />}
        </div>

        {/* Footer */}
        <footer className="cr-drawer__footer">
          <div className="cr-drawer__footer-left">
            {!isCampaignSynced && (
              <button
                className="cr-btn cr-btn--ghost cr-btn--danger"
                onClick={() => onReject?.(campaign?.id)}
                disabled={campaign?.is_protected}
                title={campaign?.is_protected ? 'This campaign is protected' : 'Reject this draft'}
              >
                Reject
              </button>
            )}
            <button
              className="cr-btn cr-btn--ghost"
              onClick={() => onArchive?.(campaign?.id)}
              disabled={campaign?.is_protected}
              title={campaign?.is_protected ? 'This campaign is protected' : 'Archive this campaign'}
            >
              Archive
            </button>
          </div>
          {/* Hide footer buttons on creative tab since each ad has its own button */}
          {activeTab !== 'creative' && (
            <div className="cr-drawer__footer-actions">
              {!isCampaignPausedOrScheduled && (
                <>
                  <button
                    className="cr-btn cr-btn--secondary"
                    onClick={() => onSave?.(campaign)}
                  >
                    Save Changes
                  </button>
                  <button
                    className="cr-btn cr-btn--primary"
                    onClick={() => {
                      // Call onSave with "schedule" action, similar to EditAdModal's Approve button
                      if (onSave) {
                        onSave(campaign, "schedule");
                      } else if (onApprove) {
                        // Fallback to onApprove if onSave doesn't support action parameter
                        onApprove(campaign?.id);
                      }
                    }}
                  >
                    Approve & Schedule
                  </button>
                </>
              )}
              {isCampaignPausedOrScheduled && (
                <div style={{ color: '#525250', fontSize: '0.875rem' }}>
                  {campaign?.status === 'PAUSED' || campaign?.status === 'paused' 
                    ? 'Campaign is paused. Cannot save or schedule.' 
                    : 'Campaign has scheduled ads. Cannot save or schedule.'}
                </div>
              )}
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default CampaignReview;
